import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import {
    SEARCH_QCM_LAB_API_BASE_URL,
    SCORING_API_BASE_URL,
    authHeaders,
    listSessionSummariesApi,
    createSessionApi,
    purgeSessionApi,
    listSelectedItemsApi,
    toggleSelectedItemApi,
    fetchNextQCMByStrategy,
} from '../../services/api';
import { useTranslation } from 'react-i18next';
import SectionContent from '../lab/SectionContent';
import RevisionStrategyInfoBar from './RevisionStrategyInfoBar';
import MobileOpenQuestionScreen from './MobileOpenQuestionScreen';
import MobileStudySideMenu from './MobileStudySideMenu';
import './MobileQCM.css';
import * as ScoringUtils from '../../utils/ScoringUtils';
import UserMetricsPanel from '../common/UserMetricsPanel';
import { useAuth } from '../../contexts/AuthContext';
import {
    SCORING_CONFIG, 
    getValidationThresholdForExam, 
    getCertaintyCoeff, 
    getSpeedCoeff, 
    getSpeedLabel, 
    getScoreLevel,
    calculateConsistency,
    calculateStreak
} from '../../config/scoringConfig';
import {
    buildKeywordsForOption,
    buildKeywordsTriplet,
    getMakeupQuestionsNbForInteraction,
    pushMakeupSessionPerInteraction,
    upsertMakeupSessionOnWrongByKeywordList,
    incrementMakeupSessionNb,
    decrementMakeupSessionNb,
    decrementMakeupSessionForCompletedQcm,
    collectAllSourceQuestionIds,
    aggregateMakeupConceptsByTriplet,
    loadMakeupSessionsForExamSession,
    recordFlashcardTipsComprehension,
    pushFlashcardDashboardVignette,
} from '../../utils/makeupSessionStorage';
import { getQcmCanonicalMetadata } from '../../utils/qcmMetadata';
import { triggerDashboardCompute } from '../../utils/dashboardApi';
import {
    DEFAULT_REVISION_STRATEGY,
    getMergedRevisionStrategyFromStorage,
    readRevisionStrategyFromStorage,
    saveRevisionStrategyToStorage,
    NO_QUESTION_FOR_STRATEGY_MESSAGE,
    fetchMakeupQcmIdFromTopSession,
    pickQcmIdForUnderpracticedTopics,
    shouldShowLeftTopicsRandomFallbackNotice,
    markLeftTopicsRandomFallbackNoticeShown,
    isLeftTopicsRandomFallbackNoticeText,
} from '../../utils/qcmInitialLoad';
import { DASHBOARD_REVISION_EVENT } from '../../utils/dashboardRevisionBridge';
import {
    alignDomainSelectionToCatalog,
    alignTaskSelectionToCatalog,
    domainSelectionIncludesName,
    taskSelectionIncludesName,
} from '../../utils/revisionCatalogAlign';

function IconQcm({ className = 'w-4 h-4' }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
            <path
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
        </svg>
    );
}

function IconFlashcard({ className = 'w-4 h-4' }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
            <path
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 5h6a2 2 0 012 2v2M6 5a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2v-5M6 5h6m6 0v4a2 2 0 01-2 2h-2m-6 3h.01M12 16h.01M8 16h.01"
            />
        </svg>
    );
}

/** Aligné sur QCMContext (évite catalogue vide si exam ≠ AWS par défaut). */
const DEFAULT_MOBILE_EXAM_ID = 'generative-ai-leader';

function parseKeywordSelection(input) {
    return [...new Set(
        String(input || '')
            .split(/[,\n]/)
            .map((x) => x.trim())
            .filter(Boolean)
    )];
}

const MobileQCM = () => {
    const { t } = useTranslation();
    const { logout, saveSelections, isGuest, user } = useAuth();
    const isPublicVisitor = !user || isGuest;
    const {
        qcmData,
        currentQCM,
        selectedOption,
        setSelectedOption,
        degreeOfCertainty,
        setDegreeOfCertainty,
        submitQCMAnswer,
        loading,
        setHoveredCertainty,
        feedback,
        error: qcmLoadError,
        setError: setQcmLoadError,
        getNextQCM,
        loadQCMContent,
        currentQCMIndex,
        totalQCMs,
        attemptCount,
        elapsedTime,
        maxTime,
        sessionContext,
        changeQuestionType,
        currentQuestionType
    } = useQCM();

    const [hoveredOption, setHoveredOption] = useState(null);
    const [markedIncorrect, setMarkedIncorrect] = useState(new Set());
    const [selectedAnswers, setSelectedAnswers] = useState(new Set());
    const [optionCertaintyArray, setOptionCertaintyArray] = useState([]);
    const [optionScoresArray, setOptionScoresArray] = useState([]);
    const [qcmStartTime, setQcmStartTime] = useState(null);
    const [displayTime, setDisplayTime] = useState(0);
    const [weaknessArray, setWeaknessArray] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showSmoke, setShowSmoke] = useState(false);
    const intervalRef = useRef(null);
    const scoreSubmittedRef = useRef(false);
    const solutionContainerRef = useRef(null); // Ref pour le conteneur de la solution
    const qcmContainerRef = useRef(null); // Ref pour le conteneur du QCM principal
    
    // Booster & Target Weakness states
    const [isBoosterActive, setIsBoosterActive] = useState(false);
    const [isTargetWeaknessActive, setIsTargetWeaknessActive] = useState(false);
    const [isReinforcementWeaknessActive, setIsReinforcementWeaknessActive] = useState(false);
    const [activeReinforcementSessionId, setActiveReinforcementSessionId] = useState(null);
    const [targetedWeakness, setTargetedWeakness] = useState(null);
    const makeupDecrementAppliedRef = useRef(false);
    const questionInteractionIndexRef = useRef(0);
    const [makeupVersion, setMakeupVersion] = useState(0);
    const [revisionStrategy, setRevisionStrategy] = useState(() =>
        getMergedRevisionStrategyFromStorage(
            sessionContext?.user,
            sessionContext?.exam,
            sessionContext?.sessionId || '111222'
        )
    );
    const [isStrategyPanelOpen, setIsStrategyPanelOpen] = useState(false);
    const [examCatalog, setExamCatalog] = useState({ domains: [], subdomains: [], tasks: [], total: 0 });
    const [showDomainModal, setShowDomainModal] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showGranularModal, setShowGranularModal] = useState(false);
    const [keywordInput, setKeywordInput] = useState('');
    const [strategySubTab, setStrategySubTab] = useState('A');
    const [strategyInfoExpanded, setStrategyInfoExpanded] = useState(false);
    const [strategyNotice, setStrategyNotice] = useState('');
    const [flashCardFlipped, setFlashCardFlipped] = useState(false);
    const [flashcardIds, setFlashcardIds] = useState([]);
    const [flashcardIndex, setFlashcardIndex] = useState(0);
    /** Mots-clés utilisés pour revision_qcm granular — affichés sur l’écran résultats uniquement */
    const [lastGranularSearchKeywords, setLastGranularSearchKeywords] = useState(null);
    const isFlashMode = (revisionStrategy?.studyMode || 'qcm') === 'flashcard';

    useEffect(() => {
        if (!isFlashMode) {
            setFlashcardIds([]);
            setFlashcardIndex(0);
        }
    }, [isFlashMode]);

    // Phone navigation state (spec: specifications/Navigation/phone_navigation.md)
    const isGlobalMenu = typeof window !== 'undefined' && window.__GLOBAL_MOBILE_MENU__ === true;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // Mobile search overlay state (#5)
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const searchInputRef = useRef(null);
    const [openQuestionPanel, setOpenQuestionPanel] = useState(null); // { initialTab: 'ask'|'history' } | null
    const [isSessionPanelOpen, setIsSessionPanelOpen] = useState(false);
    const [sessionSummaries, setSessionSummaries] = useState([]);
    const [sessionPanelLoading, setSessionPanelLoading] = useState(false);
    const [sessionPanelError, setSessionPanelError] = useState('');
    const [sessionShortLabel, setSessionShortLabel] = useState('');
    const [expandedSessionId, setExpandedSessionId] = useState(null);
    const [selectedQcmItems, setSelectedQcmItems] = useState([]);
    const [selectedFlashItems, setSelectedFlashItems] = useState([]);
    const [showSelectedQcmModal, setShowSelectedQcmModal] = useState(false);
    const [showSelectedFlashModal, setShowSelectedFlashModal] = useState(false);
    const [currentAnsweredStatus, setCurrentAnsweredStatus] = useState({
        loading: false,
        answeredInSession: false,
        answeredInExam: false,
        answeredSessionCount: 0,
        answeredExamCount: 0,
    });
    /** Évite la boucle Booster : currentQuestionType dans les deps relançait changeQuestionType en boucle. */
    const qcmPrefSyncDoneForUserRef = useRef(null);

    // Le CSS dédié gère maintenant le scroll

    // ============================================================================
    // BOOSTER & TARGET WEAKNESS - Gestion des préférences
    // ============================================================================
    
    // Charger les préférences une fois par utilisateur (pas à chaque changement de questionType)
    useEffect(() => {
        const userId = sessionContext?.user || 'anonymous_user';
        const syncKey = `pref_sync_${userId}`;
        if (qcmPrefSyncDoneForUserRef.current === syncKey) {
            return;
        }

        const prefKey = `qcm_preferences_${userId}`;
        const stored = localStorage.getItem(prefKey);

        if (!stored) {
            qcmPrefSyncDoneForUserRef.current = syncKey;
            return;
        }

        try {
            const prefs = JSON.parse(stored);
            const boosterPref = prefs.booster || false;
            const weaknessPref = prefs.targetWeakness || false;
            const reinforcementPref = prefs.reinforcementWeakness || false;

            setIsBoosterActive(boosterPref);
            setIsTargetWeaknessActive(weaknessPref);
            setIsReinforcementWeaknessActive(reinforcementPref);
            const revStored = readRevisionStrategyFromStorage(
                userId,
                sessionContext?.exam,
                sessionContext?.sessionId || '111222'
            );
            if (revStored) {
                try {
                    setRevisionStrategy({ ...DEFAULT_REVISION_STRATEGY, ...revStored });
                } catch (e) {
                    console.error('revision_strategy parse error', e);
                }
            } else if (reinforcementPref) {
                setRevisionStrategy((s) => ({ ...s, filterType: 'weakness_all' }));
            }
            console.log('📦 Loaded preferences:', prefs);

            const expectedType = boosterPref ? 'qcmBooster' : 'qcm';
            qcmPrefSyncDoneForUserRef.current = syncKey;
            if (expectedType !== currentQuestionType && changeQuestionType) {
                console.log('🔄 Updating questionType to match preferences:', expectedType);
                void changeQuestionType(expectedType);
            }
        } catch (e) {
            console.error('Error loading preferences:', e);
            qcmPrefSyncDoneForUserRef.current = syncKey;
        }
        // Ne pas dépendre de currentQuestionType : évite de relancer l’effet après changeQuestionType (boucle).
    }, [sessionContext?.user, changeQuestionType]);

    const persistRevisionStrategy = useCallback((updater) => {
        setRevisionStrategy((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
            saveRevisionStrategyToStorage(
                sessionContext?.user || 'anonymous_user',
                sessionContext?.exam,
                sessionContext?.sessionId || '111222',
                next
            );
            return next;
        });
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    /** Recharger la stratégie quand l’utilisateur, l’examen ou la session change. */
    useEffect(() => {
        const uid = sessionContext?.user || 'anonymous_user';
        const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        const sid = sessionContext?.sessionId || '111222';
        const stored = readRevisionStrategyFromStorage(uid, exam, sid);
        setRevisionStrategy({ ...DEFAULT_REVISION_STRATEGY, ...(stored || {}) });
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        const uid = sessionContext?.user || 'anonymous_user';
        const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        try {
            const raw = sessionStorage.getItem('revision_strategy_notice');
            if (!raw) return;
            sessionStorage.removeItem('revision_strategy_notice');
            const { message } = JSON.parse(raw);
            if (message == null || message === '') return;
            const m = String(message);
            if (
                isLeftTopicsRandomFallbackNoticeText(m) &&
                !shouldShowLeftTopicsRandomFallbackNotice(uid, exam)
            ) {
                return;
            }
            if (isLeftTopicsRandomFallbackNoticeText(m)) {
                markLeftTopicsRandomFallbackNoticeShown(uid, exam);
            }
            setStrategyNotice(m);
        } catch (_) {
            /* ignore */
        }
    }, [sessionContext?.user, sessionContext?.exam]);

    useEffect(() => {
        const onNotice = (ev) => {
            const m = ev.detail?.message;
            if (m == null || m === '') return;
            const uid = sessionContext?.user || 'anonymous_user';
            const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
            const text = String(m);
            if (
                isLeftTopicsRandomFallbackNoticeText(text) &&
                !shouldShowLeftTopicsRandomFallbackNotice(uid, exam)
            ) {
                return;
            }
            if (isLeftTopicsRandomFallbackNoticeText(text)) {
                markLeftTopicsRandomFallbackNoticeShown(uid, exam);
            }
            setStrategyNotice(text);
        };
        const onSetStrategy = (ev) => {
            const d = ev.detail || {};
            if (typeof d.filterType === 'string' || d.studyMode) {
                persistRevisionStrategy((prev) => {
                    const next = { ...prev, ...d };
                    if (typeof d.filterType === 'string' && d.filterType !== 'weakness_granular_tags') {
                        next.revisionTags = [];
                    }
                    return next;
                });
            }
        };
        const onNext = () => {
            void getNextQCM();
        };
        window.addEventListener('apprentissage:strategy-notice', onNotice);
        window.addEventListener('apprentissage:set-revision-strategy', onSetStrategy);
        window.addEventListener('requestNextQCM', onNext);
        return () => {
            window.removeEventListener('apprentissage:strategy-notice', onNotice);
            window.removeEventListener('apprentissage:set-revision-strategy', onSetStrategy);
            window.removeEventListener('requestNextQCM', onNext);
        };
    }, [getNextQCM, persistRevisionStrategy, sessionContext?.user, sessionContext?.exam]);

    const makeupConcepts = useMemo(
        () => aggregateMakeupConceptsByTriplet(sessionContext?.user || 'anonymous_user'),
        [sessionContext?.user, makeupVersion]
    );

    useEffect(() => {
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        fetch(
            `${SEARCH_QCM_LAB_API_BASE_URL}/api/search/exam_filter_catalog?exam_id=${encodeURIComponent(examId)}`
        )
            .then((r) => r.json())
            .then((d) => {
                if (d.success) {
                    const sub = d.subdomains || d.tasks || [];
                    setExamCatalog({
                        domains: d.domains || [],
                        subdomains: sub,
                        tasks: sub,
                        total: d.total || 0,
                    });
                }
            })
            .catch(() => {});
    }, [sessionContext?.exam]);

    const domainSelKey = (revisionStrategy.domainSelection || []).join('\u0001');
    const taskSelKey = (revisionStrategy.taskSelection || []).join('\u0001');

    useEffect(() => {
        const domains = examCatalog?.domains;
        const subs =
            examCatalog?.subdomains?.length > 0
                ? examCatalog.subdomains
                : examCatalog?.tasks || [];
        const r = revisionStrategy;
        if (r.filterType === 'manual_domain' && r.domainSelection?.length && domains?.length) {
            const aligned = alignDomainSelectionToCatalog(r.domainSelection, domains);
            if (aligned.join('\u0001') !== (r.domainSelection || []).join('\u0001')) {
                persistRevisionStrategy((p) => ({ ...p, domainSelection: aligned }));
            }
        }
        if (r.filterType === 'manual_task' && r.taskSelection?.length && subs.length) {
            const aligned = alignTaskSelectionToCatalog(r.taskSelection, subs);
            if (aligned.join('\u0001') !== (r.taskSelection || []).join('\u0001')) {
                persistRevisionStrategy((p) => ({ ...p, taskSelection: aligned }));
            }
        }
    }, [
        revisionStrategy.filterType,
        domainSelKey,
        taskSelKey,
        examCatalog?.domains,
        examCatalog?.subdomains,
        examCatalog?.tasks,
        persistRevisionStrategy,
    ]);

    useEffect(() => {
        if (!isStrategyPanelOpen) return;
        const ft = revisionStrategy.filterType;
        if (ft === 'weakness_all') setStrategySubTab('A');
        else if (ft === 'weakness_granular' || ft === 'weakness_granular_tags') setStrategySubTab('B');
        else if (ft === 'manual_domain') setStrategySubTab('C');
        else if (ft === 'manual_task') setStrategySubTab('D');
        else if (ft === 'left_topics') setStrategySubTab('E');
        else if (ft === 'selected_qcm') setStrategySubTab('F');
        else if (ft === 'selected_flashcard') setStrategySubTab('G');
        else if (ft === 'keywords_fuzzy') setStrategySubTab('H');
        else if (ft === 'unseen_qcm') setStrategySubTab('I');
        else setStrategySubTab('none');
    }, [isStrategyPanelOpen, revisionStrategy.filterType]);

    useEffect(() => {
        if (!isStrategyPanelOpen) return;
        setKeywordInput((revisionStrategy.keywordSelection || []).join(', '));
    }, [isStrategyPanelOpen, revisionStrategy.keywordSelection]);
    
    // Sauvegarder les préférences
    const savePreferences = (booster, targetWeakness, reinforcementWeakness) => {
        const userId = sessionContext?.user || 'anonymous_user';
        const prefKey = `qcm_preferences_${userId}`;
        const prefs = { booster, targetWeakness, reinforcementWeakness };
        localStorage.setItem(prefKey, JSON.stringify(prefs));
        console.log('💾 Saved preferences:', prefs);
    };
    
    // Toggle Booster
    const toggleBooster = async () => {
        const newState = !isBoosterActive;
        setIsBoosterActive(newState);
        savePreferences(newState, isTargetWeaknessActive, isReinforcementWeaknessActive);
        console.log('⚡ Booster:', newState ? 'ON' : 'OFF');
        
        // Changer le questionType dans le contexte
        const newQuestionType = newState ? 'qcmBooster' : 'qcm';
        if (changeQuestionType && newQuestionType !== currentQuestionType) {
            await changeQuestionType(newQuestionType);
        }
    };
    
    // Toggle Target Weakness
    const toggleTargetWeakness = () => {
        const newState = !isTargetWeaknessActive;
        setIsTargetWeaknessActive(newState);
        savePreferences(isBoosterActive, newState, isReinforcementWeaknessActive);
        console.log('🎯 Target Weakness:', newState ? 'ON' : 'OFF');
    };

    const loadMakeupQCMFromTopSession = useCallback(async () => {
        const userId = sessionContext?.user || 'anonymous_user';
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        setQcmLoadError(null);
        try {
            const result = await fetchMakeupQcmIdFromTopSession(examId, userId);
            if (!result) {
                setActiveReinforcementSessionId(null);
                setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                return;
            }
            setActiveReinforcementSessionId(result.makeupSessionId);
            await loadQCMContent(result.qcmId, { markInitialSnapResolved: true });
        } catch (e) {
            console.error('📱 makeup_qcm search failed:', e);
            setActiveReinforcementSessionId(null);
            setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
        }
    }, [sessionContext?.user, sessionContext?.exam, loadQCMContent, setQcmLoadError]);

    const toggleReinforcementWeakness = () => {
        const newState = !isReinforcementWeaknessActive;
        setIsReinforcementWeaknessActive(newState);
        if (!newState) {
            setActiveReinforcementSessionId(null);
        }
        savePreferences(isBoosterActive, isTargetWeaknessActive, newState);
        persistRevisionStrategy((prev) => ({
            ...prev,
            filterType: newState
                ? 'weakness_all'
                : prev.filterType === 'weakness_all'
                  ? 'none'
                  : prev.filterType,
        }));
        console.log('📱 Reinforcement Weakness:', newState ? 'ON' : 'OFF');
        if (newState) {
            loadMakeupQCMFromTopSession();
        }
    };
    
    const pickRandomFromTop = (results, topN = 8) => {
        if (!Array.isArray(results) || results.length === 0) return null;
        const pool = results.slice(0, Math.min(topN, results.length));
        return pool[Math.floor(Math.random() * pool.length)] || null;
    };

    const persistStudyMode = useCallback(
        (mode) => {
            // En passant en flashcard, les filtres QCM (weakness, left_topics, …) n’ont souvent
            // aucun FlashCard_Tips → message « no question for this strategy ». On garde
            // selected_flashcard ; sinon on bascule sur random pour charger la banque Tips.
            persistRevisionStrategy((prev) => {
                const next = { ...prev, studyMode: mode };
                if (mode === 'flashcard') {
                    const ft = String(prev?.filterType || 'none');
                    const flashOk =
                        ft === 'none' ||
                        ft === 'random' ||
                        ft === 'selected_flashcard';
                    if (!flashOk) {
                        next.filterType = 'random';
                        next.granularKeys = [];
                        next.revisionTags = [];
                        next.domainSelection = [];
                        next.taskSelection = [];
                        next.keywordSelection = [];
                        next.selectedItemIds = [];
                    }
                }
                return next;
            });
            setQcmLoadError(null);
            setSelectedOption(null);
            setSelectedAnswers(new Set());
            setMarkedIncorrect(new Set());
            if (mode === 'flashcard') {
                setFlashcardIds([]);
                setFlashcardIndex(0);
            }
            // setState est async : passer le mode voulu pour ne pas recharger en mode QCM
            // alors que l’UI vient de basculer en flashcard (et inversement).
            const t = window.setTimeout(
                () =>
                    loadNextQCM({
                        studyModeOverride: mode,
                        // Ignore un éventuel filter encore stale dans la closure
                        preferFlashBank: mode === 'flashcard',
                    }),
                0
            );
            return () => window.clearTimeout(t);
        },
        [setSelectedOption]
    );

    const openQcmStudyFromRail = useCallback(() => {
        persistStudyMode('qcm');
        setIsMenuOpen(false);
    }, [persistStudyMode]);

    const openFlashcardStudyFromRail = useCallback(() => {
        persistStudyMode('flashcard');
        setIsMenuOpen(false);
    }, [persistStudyMode]);

    const applyFlashDeck = useCallback(
        async (results, { forceFlash = false } = {}) => {
            let rows = Array.isArray(results) ? results : [];
            if (forceFlash || isFlashMode) {
                // Ne jamais réutiliser des lignes QCM comme « deck » flashcard :
                // sans filtre strict, l’API peut renvoyer des qcmBooster/qcm et
                // l’UI les affiche en layout flashcard (texte QCM tronqué).
                const only = rows.filter((x) => {
                    const qt = String(x?.question_type || x?.questionType || '').trim();
                    return qt === 'FlashCard_Tips';
                });
                rows = only;
            }
            const ids = [...new Set(
                rows
                    .map((x) => String(x?.id || x?.qcm_id || x?.FlashCard_Tips_id || '').trim())
                    .filter(Boolean)
            )].slice(0, 20);
            if (!ids.length) return false;
            setFlashcardIds(ids);
            setFlashcardIndex(0);
            await loadQCMContent(ids[0], { markInitialSnapResolved: true });
            return true;
        },
        [loadQCMContent, isFlashMode]
    );

    const goFlashcardAt = useCallback(
        async (idx) => {
            if (!flashcardIds.length) return;
            const next = Math.max(0, Math.min(flashcardIds.length - 1, idx));
            setFlashcardIndex(next);
            await loadQCMContent(flashcardIds[next], { markInitialSnapResolved: true });
        },
        [flashcardIds, loadQCMContent]
    );

    // Ref à jour pour Next flashcard (évite TDZ / deps sur loadNextQCM défini plus bas).
    const loadNextQCMRef = useRef(async () => {});

    /** Avance flashcard : deck local si possible, sinon charge un nouvel item (évite le blocage Card 1/1). */
    const advanceToNextFlashcard = useCallback(async () => {
        if (flashcardIds.length > 0 && flashcardIndex < flashcardIds.length - 1) {
            await goFlashcardAt(flashcardIndex + 1);
            return;
        }
        // Fin de deck (ou pas de deck) : libérer l’état pour ne pas rester coincé sur le dernier index
        setFlashcardIds([]);
        setFlashcardIndex(0);
        await loadNextQCMRef.current({ studyModeOverride: 'flashcard', preferFlashBank: true });
    }, [flashcardIds, flashcardIndex, goFlashcardAt]);

    useEffect(() => {
        setFlashCardFlipped(false);
    }, [currentQCM?.qcm_id, flashcardIndex, isFlashMode]);

    const handleFlashComprehension = useCallback(
        async (scoreDelta, label) => {
            const qid = currentQCM?.qcm_id || currentQCM?.FlashCard_Tips_id;
            if (!qid) return;
            const tips = currentQCM?.Tips || {};
            const kws = Array.isArray(tips.keywords)
                ? tips.keywords
                : Array.isArray(currentQCM?.keywords)
                  ? currentQCM.keywords
                  : [];
            const md = currentQCM?.metadata || {};
            recordFlashcardTipsComprehension(sessionContext?.user || 'anonymous_user', {
                sourceQuestionId: String(qid),
                scoreDelta,
                keywords: kws,
                domain: md.Domain || md.domain,
                task: md.Task || md.task,
            });
            pushFlashcardDashboardVignette(sessionContext?.user || 'anonymous_user', {
                id: String(qid),
                label,
                rating: scoreDelta,
                problem: String(currentQCM?.Tips?.ProblemToResolve || currentQCM?.qcm?.question || label || ''),
            });
            setFlashCardFlipped(false);
            await advanceToNextFlashcard();
        },
        [advanceToNextFlashcard, currentQCM, sessionContext?.user]
    );

    async function loadNextQCM(options = {}) {
        const studyModeOverride = options?.studyModeOverride;
        const preferFlashBank = Boolean(options?.preferFlashBank);
        const flashMode =
            studyModeOverride != null
                ? studyModeOverride === 'flashcard'
                : isFlashMode;
        setLastGranularSearchKeywords(null);
        setQcmLoadError(null);
        try {
            const userId = sessionContext?.user || 'anonymous_user';
            const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
            const questionType = flashMode
                ? 'FlashCard_Tips'
                : isBoosterActive
                  ? 'qcmBooster'
                  : 'qcm';
            const excludeIds = collectAllSourceQuestionIds(userId);
            // Prefer the latest persisted strategy (setState may still be flushing).
            const storedStrategy = readRevisionStrategyFromStorage(
                userId,
                examId,
                sessionContext?.sessionId || '111222'
            );
            const r = {
                ...revisionStrategy,
                ...(storedStrategy || {}),
                ...(studyModeOverride != null ? { studyMode: studyModeOverride } : {}),
            };
            if (preferFlashBank && flashMode) {
                r.filterType = 'random';
            }

            const loadFlashFromBank = async () => {
                const excludeId = String(
                    currentQCM?.qcm_id ||
                    currentQCM?.FlashCard_Tips_id ||
                    currentQCM?.Tips?.FlashCard_Tips_id ||
                    qcmData?.qcm_id ||
                    ''
                );
                // Toujours random pour la banque Tips : les stratégies session (smart_test1, …)
                // n’apportent rien ici et peuvent faire échouer le next selon le gateway.
                const strategyHint = 'random';
                try {
                    let nextId = await fetchNextQCMByStrategy(
                        examId,
                        strategyHint,
                        userId,
                        'FlashCard_Tips',
                        excludeId || null
                    );
                    if (nextId && String(nextId) === excludeId) {
                        const retryId = await fetchNextQCMByStrategy(
                            examId,
                            strategyHint,
                            userId,
                            'FlashCard_Tips',
                            null
                        );
                        if (retryId && String(retryId) !== excludeId) {
                            nextId = retryId;
                        }
                    }
                    if (!nextId) {
                        setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                        return false;
                    }
                    setFlashcardIds([String(nextId)]);
                    setFlashcardIndex(0);
                    setFlashCardFlipped(false);
                    await loadQCMContent(String(nextId), { markInitialSnapResolved: true });
                    return true;
                } catch (e) {
                    console.warn('loadFlashFromBank failed', e?.message || e);
                    setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                    return false;
                }
            };

            // Bascule QCM→flash ou Next flash : aller directement à la banque Tips
            // (évite weakness_all / left_topics encore actifs côté QCM).
            if (flashMode && (preferFlashBank || r.filterType === 'none' || r.filterType === 'random')) {
                if (await loadFlashFromBank()) return;
                return;
            }
            if ((r.filterType === 'selected_qcm' || r.filterType === 'selected_flashcard') && Array.isArray(r.selectedItemIds) && r.selectedItemIds.length > 0) {
                // selected_qcm n’a pas de sens en mode flash → banque Tips
                if (flashMode && r.filterType === 'selected_qcm') {
                    if (await loadFlashFromBank()) return;
                    return;
                }
                const currentId = String(
                    currentQCM?.qcm_id ||
                    currentQCM?.qcm?._id ||
                    currentQCM?.qcm?.id ||
                    currentQCM?.FlashCard_Tips_id ||
                    currentQCM?.Tips?.FlashCard_Tips_id ||
                    ''
                );
                const candidates = r.selectedItemIds
                    .map((x) => String(x || '').trim())
                    .filter(Boolean)
                    .filter((id) => id !== currentId);
                // Comportement historique : si plus d’autre id, on recycle la sélection (y compris l’item courant).
                const fallbackCandidates = candidates.length ? candidates : r.selectedItemIds.filter(Boolean);
                const picked = fallbackCandidates[Math.floor(Math.random() * fallbackCandidates.length)];
                if (picked) {
                    if (flashMode) {
                        const deck = fallbackCandidates.slice(0, 20);
                        setFlashcardIds(deck);
                        setFlashcardIndex(Math.max(0, deck.indexOf(String(picked))));
                    }
                    await loadQCMContent(String(picked), { markInitialSnapResolved: true });
                    return;
                }
            }

            const tryRevisionApi = async (body) => {
                const currentQcmId = String(
                    currentQCM?.qcm_id || qcmData?.qcm_id || body?.exclude_qcm_id || ''
                ).trim();
                const payload = {
                    exam_id: examId,
                    question_type: flashMode ? 'FlashCard_Tips' : 'qcm',
                    userid: userId,
                    session_id: sessionContext?.sessionId || '111222',
                    exclude_qcm_ids: [...new Set(excludeIds)],
                    ...body,
                };
                if (currentQcmId && !payload.exclude_qcm_id) {
                    payload.exclude_qcm_id = currentQcmId;
                }
                const resp = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/revision_qcm`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify(payload),
                });
                return resp.json();
            };

            /** Applique les résultats revision_qcm. En flash : uniquement FlashCard_Tips (pas de QCM en layout flash). */
            const takeRevisionResults = async (data, { flatKw = null, topN = 8 } = {}) => {
                if (!(data?.success && data.results?.length)) return false;
                if (flashMode) {
                    return applyFlashDeck(data.results, { forceFlash: true });
                }
                const picked = pickRandomFromTop(data.results, topN);
                const qid = String(
                    picked?.id ||
                    picked?.qcm_id ||
                    data.results[0]?.id ||
                    data.results[0]?.qcm_id ||
                    ''
                ).trim();
                if (!qid) return false;
                if (flatKw) setLastGranularSearchKeywords(flatKw);
                setActiveReinforcementSessionId(null);
                await loadQCMContent(qid, { markInitialSnapResolved: true });
                return true;
            };

            /** Filtre sans résultat : en QCM → erreur ; en flash → banque Tips (évite le blocage après bascule de mode). */
            const failRevisionFilter = async () => {
                if (flashMode) {
                    await loadFlashFromBank();
                    return;
                }
                setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
            };

            if (r.filterType === 'weakness_granular' && r.granularKeys?.length > 0) {
                const concepts = aggregateMakeupConceptsByTriplet(userId);
                const tags = r.granularKeys
                    .map((k) => {
                        const c = concepts.find((x) => x.key === k);
                        if (!c) return null;
                        const raw = c.searchTags || c.keywords || [];
                        const cleaned = raw.filter(
                            (x) => x && typeof x === 'string' && !String(x).startsWith('(')
                        );
                        return cleaned.length ? [...cleaned] : null;
                    })
                    .filter(Boolean);
                if (tags.length === 0) {
                    await failRevisionFilter();
                    return;
                }
                const flatKw = [...new Set(tags.flat())];
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'weakness_granular',
                        tags,
                    });
                    if (await takeRevisionResults(data, { flatKw, topN: 8 })) return;
                } catch (e) {
                    console.error('revision granular failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'weakness_granular_tags' && Array.isArray(r.revisionTags) && r.revisionTags.length > 0) {
                const tags = r.revisionTags.filter((t) => Array.isArray(t) && t.some(Boolean));
                if (!tags.length) {
                    await failRevisionFilter();
                    return;
                }
                const flatKw = [...new Set(tags.flat().map((x) => String(x).trim()).filter(Boolean))];
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'weakness_granular',
                        tags,
                    });
                    if (await takeRevisionResults(data, { flatKw, topN: 8 })) return;
                } catch (e) {
                    console.error('revision granular_tags failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'manual_domain' && r.domainSelection?.length > 0) {
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'manual_domain',
                        selection: r.domainSelection,
                    });
                    if (await takeRevisionResults(data, { topN: 10 })) return;
                } catch (e) {
                    console.error('revision domain failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'manual_task' && r.taskSelection?.length > 0) {
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'manual_task',
                        selection: r.taskSelection,
                    });
                    if (await takeRevisionResults(data, { topN: 10 })) return;
                } catch (e) {
                    console.error('revision task failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'keywords_fuzzy' && Array.isArray(r.keywordSelection) && r.keywordSelection.length > 0) {
                const selection = r.keywordSelection.map((k) => String(k || '').trim()).filter(Boolean);
                if (!selection.length) {
                    await failRevisionFilter();
                    return;
                }
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'keywords_fuzzy',
                        selection,
                        userid: userId,
                    });
                    if (await takeRevisionResults(data, { topN: 10 })) return;
                } catch (e) {
                    console.error('revision keywords_fuzzy failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'unseen_qcm') {
                try {
                    const data = await tryRevisionApi({
                        filter_type: 'unseen_qcm',
                        userid: userId,
                        session_id: sessionContext?.sessionId || '111222',
                    });
                    if (await takeRevisionResults(data, { topN: 10 })) return;
                } catch (e) {
                    console.error('revision unseen_qcm failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'left_topics') {
                try {
                    const pickedId = await pickQcmIdForUnderpracticedTopics({
                        examId,
                        userId,
                        sessionId: sessionContext?.sessionId || '111222',
                        excludeQcmId: currentQCM?.qcm_id || qcmData?.qcm_id || null,
                        questionType: flashMode ? 'FlashCard_Tips' : 'qcm',
                    });
                    if (pickedId) {
                        setActiveReinforcementSessionId(null);
                        if (flashMode) {
                            setFlashcardIds([String(pickedId)]);
                            setFlashcardIndex(0);
                        }
                        await loadQCMContent(String(pickedId), { markInitialSnapResolved: true });
                        return;
                    }
                } catch (e) {
                    console.error('revision left_topics failed', e);
                }
                await failRevisionFilter();
                return;
            }

            if (r.filterType === 'weakness_all' || isReinforcementWeaknessActive) {
                // Non-strict global weakness: best matching candidates, then random among top 8.
                const concepts = aggregateMakeupConceptsByTriplet(userId);
                const ranked = [...concepts].sort(
                    (a, b) => (Number(b.scoreSum || 0) - Number(a.scoreSum || 0))
                );
                const tags = ranked
                    .slice(0, 8)
                    .map((c) => {
                        const raw = c.searchTags || c.keywords || [];
                        const cleaned = raw.filter(
                            (x) => x && typeof x === 'string' && !String(x).startsWith('(')
                        );
                        return cleaned.length ? [...cleaned] : null;
                    })
                    .filter(Boolean);
                if (tags.length > 0) {
                    try {
                        const data = await tryRevisionApi({
                            filter_type: 'weakness_granular',
                            tags,
                        });
                        if (await takeRevisionResults(data, { flatKw: [...new Set(tags.flat())], topN: 8 })) return;
                    } catch (e) {
                        console.error('revision weakness_all fuzzy failed', e);
                    }
                }
                if (flashMode) {
                    await failRevisionFilter();
                    return;
                }
                // Fallback QCM: existing make-up top session behavior.
                await loadMakeupQCMFromTopSession();
                return;
            }
            
            // Target Weakness : en flash, ne jamais accepter un QCM à la place d’une Tips.
            if (isTargetWeaknessActive) {
                if (flashMode) {
                    await loadFlashFromBank();
                    return;
                }
                console.log('🎯 Loading weakness-targeted QCM (questionType:', questionType, ')');
                
                const response = await fetch(`${SCORING_API_BASE_URL}/api/scoring_inputs_preparation`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({
                        user_id: userId,
                        exam_id: examId,
                        question_type: questionType
                    })
                });
                
                const data = await response.json();
                
                if (data.success && data.qcm) {
                    setTargetedWeakness(data.targeted_weakness);
                    const qid =
                        data.qcm?.qcm_id ||
                        data.qcm?.id ||
                        (typeof data.qcm === 'string' ? data.qcm : null);
                    if (qid) {
                        await loadQCMContent(String(qid), { markInitialSnapResolved: true });
                    } else {
                        setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                    }
                } else {
                    setTargetedWeakness(null);
                    setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                }
                return;
            }
            
            // Mode flashcard : uniquement la banque FlashCard_Tips (jamais getNextQCM générique).
            if (flashMode) {
                await loadFlashFromBank();
                return;
            }

            // Si Booster est actif OU mode normal, utiliser getNextQCM du contexte
            // Le contexte QCM gère déjà le questionType via currentQuestionType
            console.log('📝 Loading QCM with questionType:', currentQuestionType);
            
            // S'assurer que le questionType est correct avant d'appeler getNextQCM
            if (questionType !== currentQuestionType && changeQuestionType) {
                await changeQuestionType(questionType);
            } else {
                getNextQCM();
            }
        } catch (error) {
            console.error('❌ Error loading QCM:', error);
            setQcmLoadError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
        }
    }
    loadNextQCMRef.current = loadNextQCM;

    useEffect(() => {
        const onDashboardRevision = (ev) => {
            const strategy = ev.detail?.strategy;
            if (!strategy || typeof strategy !== 'object') return;
            const next = { ...DEFAULT_REVISION_STRATEGY, ...strategy };
            saveRevisionStrategyToStorage(
                sessionContext?.user || 'anonymous_user',
                sessionContext?.exam,
                sessionContext?.sessionId || '111222',
                next
            );
            setRevisionStrategy(next);
            setQcmLoadError(null);
        };
        window.addEventListener(DASHBOARD_REVISION_EVENT, onDashboardRevision);
        return () => window.removeEventListener(DASHBOARD_REVISION_EVENT, onDashboardRevision);
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        const loadLeftTopics = async () => {
            try {
                const uid = sessionContext?.user || 'anonymous_user';
                const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
                const makeup = loadMakeupSessionsForExamSession(uid, exam, sessionContext?.sessionId);
                const dash = await triggerDashboardCompute(uid, exam, makeup, sessionContext?.sessionId);
                const rows = Array.isArray(dash?.subdomain_catalog_progress)
                    ? dash.subdomain_catalog_progress
                    : [];
                const under = rows
                    .filter((r) => {
                        const asked = Number(r?.asked_count || 0);
                        const exp = Number(r?.expected_count);
                        const target = Number.isFinite(exp) && exp > 0 ? exp : 3;
                        return asked < target;
                    })
                    .map((r) => String(r.task || '').trim())
                    .filter(Boolean);
            } catch {
            }
        };
        loadLeftTopics();
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId, currentQCMIndex]);

    /**
     * Après le 1er chargement (SmartExamRouter + qcmInitialLoad), recharge un QCM filtré
     * seulement quand l’utilisateur **modifie** la stratégie de révision (pas au montage).
     */
    const prevRevisionKeyForLoadRef = useRef(null);
    useEffect(() => {
        prevRevisionKeyForLoadRef.current = null;
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        const r = revisionStrategy;
        const key = [
            r.filterType,
            JSON.stringify(r.taskSelection || []),
            JSON.stringify(r.domainSelection || []),
            JSON.stringify(r.keywordSelection || []),
            JSON.stringify(r.granularKeys || []),
            JSON.stringify(r.revisionTags || []),
        ].join('|');

        if (prevRevisionKeyForLoadRef.current === null) {
            prevRevisionKeyForLoadRef.current = key;
            return;
        }
        if (prevRevisionKeyForLoadRef.current === key) return;
        prevRevisionKeyForLoadRef.current = key;

        const needsFilteredQcm =
            (r.filterType === 'manual_task' && r.taskSelection?.length > 0) ||
            r.filterType === 'left_topics' ||
            (r.filterType === 'manual_domain' && r.domainSelection?.length > 0) ||
            (r.filterType === 'keywords_fuzzy' && r.keywordSelection?.length > 0) ||
            r.filterType === 'unseen_qcm' ||
            (r.filterType === 'weakness_granular' && r.granularKeys?.length > 0) ||
            (r.filterType === 'weakness_granular_tags' && r.revisionTags?.length > 0) ||
            r.filterType === 'weakness_all';

        if (!needsFilteredQcm) return;

        const t = window.setTimeout(() => loadNextQCM(), 0);
        return () => window.clearTimeout(t);
    }, [revisionStrategy, sessionContext?.exam]);

    // Gérer les réponses multiples comme dans ExamQCMOptions
    const correctAnswers = useMemo(() => {
        if (!currentQCM || !currentQCM.qcm) {
            return [];
        }
        
        const normalize = (item) => {
            if (typeof item === 'string') {
                return { answer: item, explanation: '' };
            } else if (item && typeof item.answer === 'string') {
                return item;
            }
            return null;
        };
        
        let result = [];
        
        // Gestion de correct_answer (peut être un objet ou un array)
        if (Array.isArray(currentQCM.qcm.correct_answer)) {
            result = currentQCM.qcm.correct_answer.map(normalize).filter(Boolean);
        } else if (currentQCM.qcm.correct_answer) {
            const normalized = normalize(currentQCM.qcm.correct_answer);
            if (normalized) result = [normalized];
        }
        
        // Fallback pour correct_answers (au pluriel)
        if (result.length === 0 && Array.isArray(currentQCM.qcm.correct_answers)) {
            result = currentQCM.qcm.correct_answers.map(normalize).filter(Boolean);
        }
        
        console.log('🔍 Mobile QCM Debug - correctAnswers result:', result);
        return result;
    }, [currentQCM?.qcm?.correct_answer, currentQCM?.qcm?.correct_answers]);

    // Mélanger les options pour randomiser la position des bonnes réponses (EXACTEMENT comme desktop)
    const allOptions = useMemo(() => {
        if (!currentQCM || !currentQCM.qcm) {
            return [];
        }
        
        const options = [
            ...correctAnswers,
            ...(currentQCM?.qcm?.distractors || [])
        ];
        
        // Mélange Fisher-Yates pour un mélange vraiment aléatoire
        const shuffledOptions = [...options];
        for (let i = shuffledOptions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
        }
        
        console.log('🎲 Mobile QCM Options mélangées:', shuffledOptions.map(opt => opt.answer));
        return shuffledOptions;
    }, [correctAnswers, currentQCM?.qcm?.distractors]);

    // Initialize optionCertaintyArray and start time when QCM loads
    useEffect(() => {
        setOptionCertaintyArray([]);
        setOptionScoresArray([]);
        setWeaknessArray([]);
        questionInteractionIndexRef.current = 0;
        setQcmStartTime(Date.now());
        setDisplayTime(0);
        
        // Scroll to top when new QCM loads - utiliser le conteneur du QCM
        if (qcmContainerRef.current) {
            qcmContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
        
        // Start timer
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        
        intervalRef.current = setInterval(() => {
            setDisplayTime(prev => prev + 1);
        }, 1000);
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [currentQCM?.qcm_id]);

    // NOTE: do not early-return before hooks are declared below to keep hook order stable

    // Fonction pour calculer le score unitaire d'une option
    const calculateUnitaryScore = (option, certainty, elapsedTime, isCorrect) => {
        // Utiliser les valeurs de la configuration globale
        const valueOfCorrectAnswer = SCORING_CONFIG.scoring.value_correct_answer;
        const valueOfDistractor = SCORING_CONFIG.scoring.value_distractor;
        const expectedTime = maxTime || SCORING_CONFIG.scoring.expected_time_seconds;
        
        // Score de base - utiliser isCorrect passé en paramètre pour éviter les différences
        const unitaryRowScore = isCorrect ? valueOfCorrectAnswer : valueOfDistractor;
        
        const certaintyCoeff = getCertaintyCoeff(certainty);
        
        const speedPercentage = expectedTime > 0 ? (elapsedTime / expectedTime) * 100 : 0;
        const speedCoeff = getSpeedCoeff(speedPercentage);
        
        const unitaryScore = unitaryRowScore * speedCoeff;
        
        return {
            unitaryScore: unitaryScore,
            scoreBreakdown: {
                baseScore: unitaryRowScore,
                certaintyCoeff: certaintyCoeff,
                speedCoeff: speedCoeff,
                speedPercentage: speedPercentage,
                isCorrect: isCorrect
            }
        };
    };

    const handleOptionSelect = (option, certainty) => {
        const correctAnswerTexts = new Set(correctAnswers.map(ca => ca.answer));
        const isCorrect = correctAnswerTexts.has(option.answer);
        
        // Calculate elapsed time since QCM start (in seconds)
        const currentTime = Date.now();
        const optionElapsedTime = qcmStartTime ? Math.round((currentTime - qcmStartTime) / 1000) : 0;
        
        // Calculate unitary score for this option
        const scoreData = calculateUnitaryScore(option, certainty, optionElapsedTime, isCorrect);
        
        // Add to optionCertaintyArray
        const newOptionEntry = {
            is_correct: isCorrect ? 'yes' : 'no',
            certainty: certainty,
            elapsed_time: optionElapsedTime
        };
        
        setOptionCertaintyArray(prev => {
            // Remove existing entry for this option if it exists
            const filtered = prev.filter(entry => entry.option !== option.answer);
            return [...filtered, { ...newOptionEntry, option: option.answer }];
        });
        
        // Add to optionScoresArray avec toutes les métadonnées (domain / task depuis metadata)
        const qcmMeta = getQcmCanonicalMetadata(currentQCM);
        setOptionScoresArray(prev => {
            const filtered = prev.filter(entry => entry.option !== option.answer);
            return [...filtered, { 
                option: option.answer,
                unitaryScore: scoreData.unitaryScore,
                scoreBreakdown: scoreData.scoreBreakdown,
                elapsed_time: optionElapsedTime,
                domain: qcmMeta.domain,
                task: qcmMeta.task,
                topic: qcmMeta.topic,
                keywords: qcmMeta.keywords,
                qcm_id: currentQCM?.qcm_id || '',
                is_correct: isCorrect,
                certainty: certainty
            }];
        });
        
        // Weakness rules (affichage / scoring) :
        // 1) Distracteur : ajouter les bonnes réponses non encore sélectionnées.
        if (!isCorrect) {
            const unselectedCorrectOptions = correctAnswers
                .map(ca => ca.answer)
                .filter(answer => !selectedAnswers.has(answer));
            
            setWeaknessArray(prev => {
                const newWeaknesses = [...prev, ...unselectedCorrectOptions];
                return [...new Set(newWeaknesses)];
            });
        }
        // 2) Bonne réponse + certitude low/medium : renforcer la trace pédagogique.
        if (isCorrect && (certainty === 'low' || certainty === 'medium')) {
            setWeaknessArray(prev => [...new Set([...prev, option.answer])]);
        }

        // Make-up sessions (spec weakness_reinforcement v2 : matrice par clic, sessions distinctes).
        const { domain, task } = getQcmCanonicalMetadata(currentQCM);
        const sourceId = String(currentQCM?.qcm_id || '');
        const userId = sessionContext?.user || 'anonymous_user';

        questionInteractionIndexRef.current += 1;
        const interactionIndex = questionInteractionIndexRef.current;

        if (isReinforcementWeaknessActive && activeReinforcementSessionId && !isCorrect) {
            incrementMakeupSessionNb(userId, activeReinforcementSessionId);
            setMakeupVersion((v) => v + 1);
        } else if (!isReinforcementWeaknessActive && sourceId) {
            if (!isCorrect) {
                const keywordsFromCorrect = buildKeywordsTriplet(currentQCM, correctAnswers);
                const updated = upsertMakeupSessionOnWrongByKeywordList(userId, {
                    sourceQuestionId: sourceId,
                    keywords: keywordsFromCorrect,
                    domain,
                    task,
                    examId: sessionContext?.exam,
                    sessionId: sessionContext?.sessionId,
                });
                if (updated) {
                    setMakeupVersion((v) => v + 1);
                }
            } else {
                const nb = getMakeupQuestionsNbForInteraction(isCorrect, certainty);
                const keywordsForOption = buildKeywordsForOption(option, currentQCM, correctAnswers);
                const created = pushMakeupSessionPerInteraction(userId, {
                    sourceQuestionId: sourceId,
                    domain,
                    task,
                    interactionIndex,
                    certainty,
                    keywords: keywordsForOption,
                    makeupQuestionsNb: nb,
                    examId: sessionContext?.exam,
                    sessionId: sessionContext?.sessionId,
                });
                if (created) {
                    setMakeupVersion((v) => v + 1);
                }
            }
        }

        // Visual feedback effects
        if (isCorrect) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 2000);
        } else {
            setShowSmoke(true);
            setTimeout(() => setShowSmoke(false), 2000);
        }
        
        if (correctAnswers.length > 1) {
            // Mode multi-réponses : ajouter à la sélection
            setSelectedAnswers(prev => {
                const newSet = new Set(prev);
                newSet.add(option.answer);
                return newSet;
            });
            setDegreeOfCertainty(certainty);
            
            // Vérifier si toutes les bonnes réponses sont sélectionnées
            const newSelectedAnswers = new Set([...selectedAnswers, option.answer]);
            const allCorrectSelected = [...correctAnswerTexts].every(ca => newSelectedAnswers.has(ca));
            
            if (allCorrectSelected) {
                // Toutes les bonnes réponses sont sélectionnées, soumettre
                setTimeout(() => {
                    // Soumettre avec la première réponse sélectionnée (pour compatibilité API)
                    submitQCMAnswer(option.answer, certainty);
                }, 100);
            }
        } else {
            // Mode réponse unique
            setSelectedOption(option.answer);
            setDegreeOfCertainty(certainty);
            
            // Auto-submit with selected certainty (EXACTEMENT comme desktop)
            setTimeout(() => {
                submitQCMAnswer(option.answer, certainty);
            }, 100);
        }
    };

    /** Replier le détail du bandeau Strategy et fermer le panneau modal au changement d’écran. */
    useEffect(() => {
        const onNav = () => {
            setStrategyInfoExpanded(false);
            setIsStrategyPanelOpen(false);
        };
        window.addEventListener('nav_request', onNav);
        return () => window.removeEventListener('nav_request', onNav);
    }, []);

    const dispatchNavigate = (page) => {
        if (!page) return;
        try {
            window.dispatchEvent(new CustomEvent('nav_request', { detail: { page } }));
        } catch (e) {
            // no-op
        }
    };

    const handleMenuAction = (action, event) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        if (!isGlobalMenu && (action === 'qcm' || action === 'search')) {
            setStrategyInfoExpanded(false);
            setIsStrategyPanelOpen(false);
            setIsSearchOpen(true);
            setTimeout(() => setIsMenuOpen(false), 0);
        } else if (!isGlobalMenu && action === 'what_is') {
            setStrategyInfoExpanded(false);
            setIsStrategyPanelOpen(false);
            setIsSearchOpen(false);
            setOpenQuestionPanel({ initialTab: 'ask' });
            setTimeout(() => setIsMenuOpen(false), 0);
        } else if (!isGlobalMenu && action === 'what_is_list') {
            setStrategyInfoExpanded(false);
            setIsStrategyPanelOpen(false);
            setIsSearchOpen(false);
            setOpenQuestionPanel({ initialTab: 'history' });
            setTimeout(() => setIsMenuOpen(false), 0);
        } else if (!isGlobalMenu && action === 'session') {
            setStrategyInfoExpanded(false);
            setIsStrategyPanelOpen(false);
            setIsSearchOpen(false);
            setOpenQuestionPanel(null);
            setIsSessionPanelOpen(true);
            setTimeout(() => setIsMenuOpen(false), 0);
        } else {
            if (!isGlobalMenu) {
                setIsMenuOpen(false);
            }

            switch (action) {
                case 'exam':
                case 'dashboard':
                case 'home':
                case 'notes':
                    dispatchNavigate(action);
                    break;
                case 'disconnect':
                    if (typeof logout === 'function') {
                        try {
                            logout();
                        } catch (err) {
                            console.error('Logout error from phone menu:', err);
                        }
                    }
                    dispatchNavigate('home');
                    break;
                default:
                    break;
            }
        }

        try {
            window.dispatchEvent(new CustomEvent('phone_navigation', { detail: { action } }));
        } catch (e) {
            // no-op
        }
    };

    const loadSessionSummaries = useCallback(async () => {
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        if (!examId) return;
        setSessionPanelLoading(true);
        setSessionPanelError('');
        try {
            const data = await listSessionSummariesApi(examId);
            const rows = Array.isArray(data?.sessions) ? data.sessions : [];
            setSessionSummaries(rows);
            if (!expandedSessionId && rows[0]?.sessionId) {
                setExpandedSessionId(rows[0].sessionId);
            }
        } catch (e) {
            setSessionPanelError(e?.response?.data?.error || 'Unable to load sessions.');
        } finally {
            setSessionPanelLoading(false);
        }
    }, [sessionContext?.exam, expandedSessionId]);

    const activateSession = useCallback(async (sessionId) => {
        if (!sessionId) return;
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        const strategyId = revisionStrategy?.strategyId || sessionContext?.strategy || null;
        const result = await saveSelections({ examId, strategyId, sessionId });
        if (!result?.success) {
            throw new Error(result?.error || 'Unable to activate session.');
        }
        window.location.reload();
    }, [revisionStrategy?.strategyId, saveSelections, sessionContext?.exam, sessionContext?.strategy]);

    const createNewSession = useCallback(async () => {
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        const strategyId = revisionStrategy?.strategyId || sessionContext?.strategy || null;
        const shortLabel = String(sessionShortLabel || '').trim().slice(0, 12);
        if (!shortLabel) {
            setSessionPanelError('Short label (max 12 chars) is required.');
            return;
        }
        setSessionPanelError('');
        setSessionPanelLoading(true);
        try {
            const created = await createSessionApi({ examId, strategyId, name: shortLabel });
            const sid = created?.session?.sessionId;
            if (sid) {
                await activateSession(sid);
                return;
            }
            await loadSessionSummaries();
            setSessionShortLabel('');
        } catch (e) {
            setSessionPanelError(e?.response?.data?.error || 'Unable to create session.');
        } finally {
            setSessionPanelLoading(false);
        }
    }, [activateSession, loadSessionSummaries, revisionStrategy?.strategyId, sessionContext?.exam, sessionContext?.strategy, sessionShortLabel]);

    const deleteSessionWithData = useCallback(async (sessionId) => {
        if (!sessionId) return;
        const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        setSessionPanelError('');
        setSessionPanelLoading(true);
        try {
            await purgeSessionApi(sessionId, examId);
            await loadSessionSummaries();
        } catch (e) {
            setSessionPanelError(e?.response?.data?.error || 'Unable to delete session.');
        } finally {
            setSessionPanelLoading(false);
        }
    }, [loadSessionSummaries, sessionContext?.exam]);

    const loadSelectedItems = useCallback(async () => {
        const examId = sessionContext?.exam;
        const sessionId = sessionContext?.sessionId;
        if (!examId || !sessionId) return;
        try {
            const [qcm, flash] = await Promise.all([
                listSelectedItemsApi({ sessionId, examId, itemType: 'qcm' }),
                listSelectedItemsApi({ sessionId, examId, itemType: 'flashcard' }),
            ]);
            setSelectedQcmItems(Array.isArray(qcm?.items) ? qcm.items : []);
            setSelectedFlashItems(Array.isArray(flash?.items) ? flash.items : []);
        } catch (_) {
            setSelectedQcmItems([]);
            setSelectedFlashItems([]);
        }
    }, [sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        loadSelectedItems();
    }, [loadSelectedItems]);

    useEffect(() => {
        if (isSessionPanelOpen) {
            loadSessionSummaries();
        }
    }, [isSessionPanelOpen, loadSessionSummaries]);

    const performMobileSearch = async () => {
        const text = searchText.trim();
        if (text.length < 3) {
            setSearchResults([]);
            setSearchError(null);
            return;
        }
        setSearchLoading(true);
        setSearchError(null);
        try {
            const payload = {
                userid: (sessionContext?.user) || 'anonymous_user',
                exam_id: (sessionContext?.exam) || DEFAULT_MOBILE_EXAM_ID,
                text: text,
                questionType: isFlashMode ? 'FlashCard_Tips' : 'qcm',
            };
            const resp = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/qcm_or_instruction`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload)
            });
            const data = await resp.json();
            if (data.success) {
                setSearchResults(data.results || []);
            } else {
                setSearchResults([]);
                setSearchError(data.error || 'Search error');
            }
        } catch (err) {
            setSearchResults([]);
            setSearchError('Connection error');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelectSearchResult = async (result) => {
        try {
            window.dispatchEvent(new CustomEvent('loadSpecificQCMFromSearch', { detail: { qcmId: result.id } }));
        } finally {
            setIsSearchOpen(false);
            setSearchText('');
            setSearchResults([]);
            setSearchError(null);
        }
    };

    useEffect(() => {
        if (isSearchOpen) {
            setTimeout(() => {
                try { searchInputRef.current?.focus(); } catch (_) {}
            }, 30);
        }
    }, [isSearchOpen]);

    // Perform QCM search using same backend endpoints as desktop SearchBox (#5)
    // (Search overlay removed as per rollback)

    const makeIncorrectOptionKey = (optionAnswer, optionIndex) => `${optionIndex}::${String(optionAnswer || '')}`;

    const handleToggleIncorrect = (optionKey, isMarked) => {
        setMarkedIncorrect(prev => {
            const newSet = new Set(prev);
            if (isMarked) {
                newSet.add(optionKey);
            } else {
                newSet.delete(optionKey);
            }
            return newSet;
        });
    };

    const toggleCurrentSelectionPin = useCallback(async () => {
        const examId = sessionContext?.exam;
        const sessionId = sessionContext?.sessionId;
        const itemId = String(
            currentQCM?.qcm_id ||
            currentQCM?.qcm?._id ||
            currentQCM?.qcm?.id ||
            currentQCM?.FlashCard_Tips_id ||
            currentQCM?.Tips?.FlashCard_Tips_id ||
            ''
        );
        if (!examId || !sessionId || !itemId) return;
        if (isPublicVisitor) {
            setQcmLoadError('Sign in to save favorites for this session.');
            return;
        }
        const itemType = isFlashMode ? 'flashcard' : 'qcm';
        const label = String(currentQCM?.qcm?.question || currentQCM?.Tips?.ProblemToResolve || '').slice(0, 180);
        try {
            await toggleSelectedItemApi({
                sessionId,
                examId,
                itemId,
                itemType,
                label,
                problem: label,
            });
            await loadSelectedItems();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.response?.data?.error;
            if (msg) setQcmLoadError(String(msg));
        }
    }, [currentQCM, isFlashMode, isPublicVisitor, loadSelectedItems, sessionContext?.exam, sessionContext?.sessionId]);

    useEffect(() => {
        let cancelled = false;
        const userId = sessionContext?.user;
        const examId = sessionContext?.exam;
        const sessionId = sessionContext?.sessionId || '111222';
        const qcmId = String(
            currentQCM?.qcm_id ||
            currentQCM?.qcm?._id ||
            currentQCM?.qcm?.id ||
            ''
        ).trim();
        if (!userId || !examId || !qcmId) {
            setCurrentAnsweredStatus({
                loading: false,
                answeredInSession: false,
                answeredInExam: false,
                answeredSessionCount: 0,
                answeredExamCount: 0,
            });
            return () => {
                cancelled = true;
            };
        }
        setCurrentAnsweredStatus((s) => ({ ...s, loading: true }));
        (async () => {
            try {
                const resp = await fetch(`${SCORING_API_BASE_URL}/api/qcm_answer_status`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({
                        user_id: userId,
                        exam_id: examId,
                        session_id: sessionId,
                        qcm_id: qcmId,
                    }),
                });
                const data = await resp.json();
                if (cancelled) return;
                if (data?.success && data?.status) {
                    const st = data.status;
                    setCurrentAnsweredStatus({
                        loading: false,
                        answeredInSession: Boolean(st.answered_in_session),
                        answeredInExam: Boolean(st.answered_in_exam),
                        answeredSessionCount: Number(st.answered_session_count || 0),
                        answeredExamCount: Number(st.answered_exam_count || 0),
                    });
                } else {
                    setCurrentAnsweredStatus((s) => ({ ...s, loading: false }));
                }
            } catch (_) {
                if (!cancelled) {
                    setCurrentAnsweredStatus((s) => ({ ...s, loading: false }));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentQCM?.qcm_id, currentQCM?.qcm?._id, currentQCM?.qcm?.id, sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    const getCertaintyColor = (level) => {
        switch (level) {
            case 'low': return 'bg-red-500';
            case 'medium': return 'bg-yellow-500';
            case 'high': return 'bg-green-500';
            default: return 'bg-gray-600';
        }
    };

    // Calculate score using the new unitary system
    const calculateScore = () => {
        if (optionScoresArray.length === 0) {
            return {
                score: 0,
                maxScore: 10,
                incorrectAnswers: 0,
                averageCertaintyCorrect: 0,
                averageCertaintyIncorrect: 0,
                speed: 0,
                speedText: "Normal",
                averageCertaintyCorrectText: "None",
                averageCertaintyIncorrectText: "None",
                uniqueWeaknesses: [],
                certaintyCounts: { correct: { high: 0, medium: 0, low: 0 }, incorrect: { high: 0, medium: 0, low: 0 } },
                attempts: 0,
                qcmIsValid: 0,
                scoreLevelText: "Débutant" // NOUVEAU
            };
        }

        const expectedTime = maxTime || 300;
        const maxScore = correctAnswers.length * 2; // valueOfCorrectAnswer = 2

        // Use unitary scores from optionScoresArray
        const totalUnitaryScore = optionScoresArray.reduce((sum, opt) => sum + opt.unitaryScore, 0);
        
        // Final score: round(A * 10 / max_score, 1)
        const score = Math.round((totalUnitaryScore * 10 / maxScore) * 10) / 10;

        // Calculate statistics from optionScoresArray
        let correctAnswersCount = 0;
        let incorrectAnswersCount = 0;
        let totalCertaintyCorrect = 0;
        let totalCertaintyIncorrect = 0;

        const certaintyCounts = {
            correct: { high: 0, medium: 0, low: 0 },
            incorrect: { high: 0, medium: 0, low: 0 }
        };
        
        optionScoresArray.forEach(option => {
            if (option.is_correct) {
                correctAnswersCount++;
                totalCertaintyCorrect += option.scoreBreakdown.certaintyCoeff;
                certaintyCounts.correct[option.certainty]++;
            } else {
                incorrectAnswersCount++;
                totalCertaintyIncorrect += option.scoreBreakdown.certaintyCoeff;
                certaintyCounts.incorrect[option.certainty]++;
            }
        });

        // Statistics
        const averageCertaintyCorrect = correctAnswersCount > 0 ? totalCertaintyCorrect / correctAnswersCount : 0;
        const averageCertaintyIncorrect = incorrectAnswersCount > 0 ? totalCertaintyIncorrect / incorrectAnswersCount : 0;
        
        // Get unique weakness values
        const uniqueWeaknesses = [...new Set(weaknessArray)];
        
        // Speed logic - utilise la configuration globale
        const lastElapsedTime = optionScoresArray.length > 0 ? 
            Math.max(...optionScoresArray.map(opt => opt.elapsed_time)) : 0;
        const speedPercentage = expectedTime > 0 ? Math.round((lastElapsedTime / expectedTime) * 100) : 0;
        const speedText = getSpeedLabel(speedPercentage);
        
        // Convert certainty coefficients to text values
        const getCertaintyText = (coeff) => {
            if (coeff >= 0.9) return "High";
            if (coeff >= 0.5) return "Medium";
            return "Low";
        };
        
        const averageCertaintyCorrectText = correctAnswersCount > 0 ? getCertaintyText(totalCertaintyCorrect / correctAnswersCount) : "None";
        const averageCertaintyIncorrectText = incorrectAnswersCount > 0 ? getCertaintyText(totalCertaintyIncorrect / incorrectAnswersCount) : "None";

        // Attempts rule: attempts = number of wrong selections + 1
        const attempts = incorrectAnswersCount + 1;

        // Validation rule: utilise le seuil de la configuration
        const validationThreshold = getValidationThresholdForExam(sessionContext?.exam);
        const qcmIsValid = score >= validationThreshold ? 1 : 0;

        // Logique pour les tranches de score - utilise la configuration
        const scoreLevel = getScoreLevel(score);
        const scoreLevelText = scoreLevel.label;

        return { 
            score,
            maxScore: 10, // Score is always on a scale of 0-10
            incorrectAnswers: incorrectAnswersCount,
            averageCertaintyCorrect,
            averageCertaintyIncorrect,
            speed: speedPercentage,
            speedText,
            averageCertaintyCorrectText,
            averageCertaintyIncorrectText,
            uniqueWeaknesses,
            certaintyCounts,
            attempts,
            qcmIsValid,
            scoreLevelText // NOUVEAU
        };
    };

    // Fonction de test pour valider les calculs
    const runScoreValidationTests = () => {
        console.log('🧪 === TESTS DE VALIDATION DES SCORES ===');
        
        // Test 1: Comparer les paramètres utilisés
        const valueOfCorrectAnswer = 2;
        const valueOfDistractor = -1.5;
        const expectedTime = maxTime || 300;
        
        console.log('📊 Paramètres:', {
            valueOfCorrectAnswer,
            valueOfDistractor,
            expectedTime,
            maxScore: correctAnswers.length * valueOfCorrectAnswer
        });
        
        // Test 2: Comparer chaque option individuellement
        console.log('🔍 Comparaison option par option:');
        optionCertaintyArray.forEach((option, index) => {
            const optionScore = optionScoresArray.find(opt => opt.option === option.option);
            
            if (optionScore) {
                // Recalculer avec la même logique que calculateScore
                const unitaryRowScore = option.is_correct === 'yes' ? valueOfCorrectAnswer : valueOfDistractor;
                
                const certaintyCoeff = getCertaintyCoeff(option.certainty);
                
                const speedPercentage = expectedTime > 0 ? (option.elapsed_time / expectedTime) * 100 : 0;
                let speedCoeff;
                if (speedPercentage <= 15) {
                    speedCoeff = 1.0; // Fast
                } else if (speedPercentage < 90) {
                    speedCoeff = 0.95; // Normal
                } else if (speedPercentage <= 100) {
                    speedCoeff = 0.9; // Limit
                } else if (speedPercentage <= 130) {
                    speedCoeff = 0.75; // Slow
                } else {
                    speedCoeff = 0.6; // Very slow
                }
                const expectedUnitaryScore = unitaryRowScore * speedCoeff;
                
                console.log(`  Option ${index + 1} (${option.option.substring(0, 20)}...):`, {
                    isCorrect: option.is_correct,
                    certainty: option.certainty,
                    elapsedTime: option.elapsed_time,
                    baseScore: unitaryRowScore,
                    certaintyCoeff: certaintyCoeff,
                    speedCoeff: speedCoeff,
                    expectedScore: expectedUnitaryScore,
                    actualScore: optionScore.unitaryScore,
                    difference: Math.abs(expectedUnitaryScore - optionScore.unitaryScore)
                });
            }
        });
        
        // Test 3: Comparer les sommes totales
        const originalTotal = optionCertaintyArray.reduce((sum, option) => {
            const unitaryRowScore = option.is_correct === 'yes' ? valueOfCorrectAnswer : valueOfDistractor;
            
            const speedPercentage = expectedTime > 0 ? (option.elapsed_time / expectedTime) * 100 : 0;
            let speedCoeff;
            if (speedPercentage <= 15) {
                speedCoeff = 1.0; // Fast
            } else if (speedPercentage < 90) {
                speedCoeff = 0.95; // Normal
            } else if (speedPercentage <= 100) {
                speedCoeff = 0.9; // Limit
            } else if (speedPercentage <= 130) {
                speedCoeff = 0.75; // Slow
            } else {
                speedCoeff = 0.6; // Very slow
            }
            return sum + unitaryRowScore * speedCoeff;
        }, 0);
        
        const unitaryTotal = optionScoresArray.reduce((sum, opt) => sum + opt.unitaryScore, 0);
        
        console.log('📈 Sommes totales:', {
            originalTotal: originalTotal,
            unitaryTotal: unitaryTotal,
            difference: Math.abs(originalTotal - unitaryTotal)
        });
        
        // Test 4: Vérifier les scores finaux sur 10
        const maxScore = correctAnswers.length * valueOfCorrectAnswer;
        const originalFinalScore = Math.round((originalTotal * 10 / maxScore) * 10) / 10;
        const unitaryFinalScore = Math.round((unitaryTotal * 10 / maxScore) * 10) / 10;
        
        console.log('🎯 Scores finaux sur 10:', {
            originalFinalScore: originalFinalScore,
            unitaryFinalScore: unitaryFinalScore,
            difference: Math.abs(originalFinalScore - unitaryFinalScore),
            maxScore: maxScore
        });
        
        console.log('🧪 === FIN DES TESTS ===');
    };

    // Fonction de test avec des données simulées pour identifier le problème
    const runSimulatedTests = () => {
        console.log('🧪 === TESTS SIMULÉS DÉTAILLÉS ===');
        
        // Test 1: Cas simple - 1 bonne réponse, 1 mauvaise
        console.log('📋 Test 1: Cas simple (1 bonne, 1 mauvaise)');
        const test1 = [
            { option: "Bonne réponse", is_correct: "yes", certainty: "high", elapsed_time: 30 },
            { option: "Mauvaise réponse", is_correct: "no", certainty: "medium", elapsed_time: 45 }
        ];
        runSingleTest(test1, 1, "Test 1");
        
        // Test 2: Cas avec temps très long
        console.log('📋 Test 2: Temps très long');
        const test2 = [
            { option: "Bonne réponse", is_correct: "yes", certainty: "high", elapsed_time: 600 },
            { option: "Mauvaise réponse", is_correct: "no", certainty: "low", elapsed_time: 700 }
        ];
        runSingleTest(test2, 1, "Test 2");
        
        // Test 3: Cas avec certitude faible
        console.log('📋 Test 3: Certitude faible');
        const test3 = [
            { option: "Bonne réponse", is_correct: "yes", certainty: "low", elapsed_time: 60 },
            { option: "Mauvaise réponse", is_correct: "no", certainty: "low", elapsed_time: 90 }
        ];
        runSingleTest(test3, 1, "Test 3");
        
        // Test 4: Cas multi-réponses
        console.log('📋 Test 4: Multi-réponses (2 bonnes, 1 mauvaise)');
        const test4 = [
            { option: "Bonne réponse 1", is_correct: "yes", certainty: "high", elapsed_time: 30 },
            { option: "Bonne réponse 2", is_correct: "yes", certainty: "medium", elapsed_time: 45 },
            { option: "Mauvaise réponse", is_correct: "no", certainty: "high", elapsed_time: 60 }
        ];
        runSingleTest(test4, 2, "Test 4");
        
        // Test 5: Cas extrême - temps très court
        console.log('📋 Test 5: Temps très court');
        const test5 = [
            { option: "Bonne réponse", is_correct: "yes", certainty: "high", elapsed_time: 5 },
            { option: "Mauvaise réponse", is_correct: "no", certainty: "high", elapsed_time: 10 }
        ];
        runSingleTest(test5, 1, "Test 5");
        
        console.log('🧪 === FIN DES TESTS SIMULÉS ===');
    };
    
    const runSingleTest = (testOptions, correctAnswersCount, testName) => {
        const valueOfCorrectAnswer = 2;
        const valueOfDistractor = -1.5;
        const expectedTime = 300;
        const maxScore = correctAnswersCount * valueOfCorrectAnswer;
        
        console.log(`  📊 ${testName} - Données:`, testOptions);
        
        // Méthode 1: Calcul traditionnel (comme dans calculateScore)
        let method1Total = 0;
        testOptions.forEach((option, index) => {
            const unitaryRowScore = option.is_correct === 'yes' ? valueOfCorrectAnswer : valueOfDistractor;
            
            const certaintyCoeff = getCertaintyCoeff(option.certainty);
            
            const speedPercentage = expectedTime > 0 ? (option.elapsed_time / expectedTime) * 100 : 0;
            let speedCoeff;
            if (speedPercentage <= 15) {
                speedCoeff = 1.0; // Fast
            } else if (speedPercentage < 90) {
                speedCoeff = 0.95; // Normal
            } else if (speedPercentage <= 100) {
                speedCoeff = 0.9; // Limit
            } else if (speedPercentage <= 130) {
                speedCoeff = 0.75; // Slow
            } else {
                speedCoeff = 0.6; // Very slow
            }
            const unitaryScore = unitaryRowScore * speedCoeff;
            method1Total += unitaryScore;
            
            console.log(`    Option ${index + 1} (Méthode 1):`, {
                baseScore: unitaryRowScore,
                certaintyCoeff: certaintyCoeff,
                speedCoeff: speedCoeff.toFixed(4),
                unitaryScore: unitaryScore.toFixed(4)
            });
        });
        
        // Méthode 2: Calcul unitaire (comme dans calculateUnitaryScore)
        let method2Total = 0;
        testOptions.forEach((option, index) => {
            const isCorrect = option.is_correct === 'yes';
            const scoreData = calculateUnitaryScore(
                { answer: option.option }, 
                option.certainty, 
                option.elapsed_time, 
                isCorrect
            );
            method2Total += scoreData.unitaryScore;
            
            console.log(`    Option ${index + 1} (Méthode 2):`, {
                baseScore: scoreData.scoreBreakdown.baseScore,
                certaintyCoeff: scoreData.scoreBreakdown.certaintyCoeff,
                speedCoeff: scoreData.scoreBreakdown.speedCoeff.toFixed(4),
                unitaryScore: scoreData.unitaryScore.toFixed(4)
            });
        });
        
        // Comparaison
        const method1FinalScore = Math.round((method1Total * 10 / maxScore) * 10) / 10;
        const method2FinalScore = Math.round((method2Total * 10 / maxScore) * 10) / 10;
        
        console.log(`  📈 ${testName} - Comparaison:`, {
            method1Total: method1Total.toFixed(4),
            method2Total: method2Total.toFixed(4),
            method1FinalScore: method1FinalScore,
            method2FinalScore: method2FinalScore,
            totalDifference: Math.abs(method1Total - method2Total),
            finalDifference: Math.abs(method1FinalScore - method2FinalScore),
            maxScore: maxScore
        });
        
        if (Math.abs(method1Total - method2Total) > 0.0001) {
            console.log(`  ⚠️  DIVERGENCE DÉTECTÉE dans ${testName}!`);
        } else {
            console.log(`  ✅ ${testName} - Pas de divergence`);
        }
    };

    // Test en temps réel avec les données actuelles
    const runRealTimeTest = () => {
        console.log('🔥 === TEST EN TEMPS RÉEL ===');
        
        if (optionCertaintyArray.length === 0) {
            console.log('❌ Aucune donnée disponible pour le test');
            return;
        }
        
        const valueOfCorrectAnswer = 2;
        const valueOfDistractor = -1.5;
        const expectedTime = maxTime || 300;
        const maxScore = correctAnswers.length * valueOfCorrectAnswer;
        
        console.log('📊 Données actuelles:', {
            optionCertaintyArray: optionCertaintyArray,
            optionScoresArray: optionScoresArray,
            correctAnswers: correctAnswers,
            maxTime: maxTime
        });
        
        // Méthode 1: Calcul traditionnel (calculateScore)
        let method1Total = 0;
        optionCertaintyArray.forEach((option, index) => {
            const unitaryRowScore = option.is_correct === 'yes' ? valueOfCorrectAnswer : valueOfDistractor;
            
            const certaintyCoeff = getCertaintyCoeff(option.certainty);
            
            const speedPercentage = expectedTime > 0 ? (option.elapsed_time / expectedTime) * 100 : 0;
            let speedCoeff;
            if (speedPercentage <= 15) {
                speedCoeff = 1.0; // Fast
            } else if (speedPercentage < 90) {
                speedCoeff = 0.95; // Normal
            } else if (speedPercentage <= 100) {
                speedCoeff = 0.9; // Limit
            } else if (speedPercentage <= 130) {
                speedCoeff = 0.75; // Slow
            } else {
                speedCoeff = 0.6; // Very slow
            }
            const unitaryScore = unitaryRowScore * speedCoeff;
            method1Total += unitaryScore;
            
            console.log(`  Méthode 1 - Option ${index + 1}:`, {
                option: option.option,
                isCorrect: option.is_correct,
                certainty: option.certainty,
                elapsedTime: option.elapsed_time,
                baseScore: unitaryRowScore,
                certaintyCoeff: certaintyCoeff,
                speedCoeff: speedCoeff.toFixed(4),
                unitaryScore: unitaryScore.toFixed(4)
            });
        });
        
        // Méthode 2: Calcul unitaire (calculateUnitaryScore)
        let method2Total = 0;
        optionScoresArray.forEach((option, index) => {
            method2Total += option.unitaryScore;
            
            console.log(`  Méthode 2 - Option ${index + 1}:`, {
                option: option.option,
                isCorrect: option.scoreBreakdown.isCorrect,
                elapsedTime: option.elapsed_time,
                baseScore: option.scoreBreakdown.baseScore,
                certaintyCoeff: option.scoreBreakdown.certaintyCoeff,
                speedCoeff: option.scoreBreakdown.speedCoeff.toFixed(4),
                unitaryScore: option.unitaryScore.toFixed(4)
            });
        });
        
        // Scores finaux
        const method1FinalScore = Math.round((method1Total * 10 / maxScore) * 10) / 10;
        const method2FinalScore = Math.round((method2Total * 10 / maxScore) * 10) / 10;
        
        console.log('📈 Résultats en temps réel:', {
            method1Total: method1Total.toFixed(4),
            method2Total: method2Total.toFixed(4),
            method1FinalScore: method1FinalScore,
            method2FinalScore: method2FinalScore,
            totalDifference: Math.abs(method1Total - method2Total),
            finalDifference: Math.abs(method1FinalScore - method2FinalScore),
            maxScore: maxScore
        });
        
        if (Math.abs(method1Total - method2Total) > 0.0001) {
            console.log('⚠️  DIVERGENCE DÉTECTÉE EN TEMPS RÉEL!');
            
            // Analyse détaillée des différences
            optionCertaintyArray.forEach((option, index) => {
                const optionScore = optionScoresArray.find(opt => opt.option === option.option);
                if (optionScore) {
                    const unitaryRowScore = option.is_correct === 'yes' ? valueOfCorrectAnswer : valueOfDistractor;
                    
                    const speedPercentage = expectedTime > 0 ? (option.elapsed_time / expectedTime) * 100 : 0;
                    let speedCoeff;
                    if (speedPercentage <= 15) {
                        speedCoeff = 1.0; // Fast
                    } else if (speedPercentage < 90) {
                        speedCoeff = 0.95; // Normal
                    } else if (speedPercentage <= 100) {
                        speedCoeff = 0.9; // Limit
                    } else if (speedPercentage <= 130) {
                        speedCoeff = 0.75; // Slow
                    } else {
                        speedCoeff = 0.6; // Very slow
                    }
                    const expectedUnitaryScore = unitaryRowScore * speedCoeff;
                    
                    console.log(`  🔍 Analyse détaillée Option ${index + 1}:`, {
                        option: option.option,
                        expectedScore: expectedUnitaryScore.toFixed(4),
                        actualScore: optionScore.unitaryScore.toFixed(4),
                        difference: Math.abs(expectedUnitaryScore - optionScore.unitaryScore).toFixed(4)
                    });
                }
            });
        } else {
            console.log('✅ Pas de divergence en temps réel');
        }
        
        console.log('🔥 === FIN TEST EN TEMPS RÉEL ===');
    };

    // Calculate score only when feedback is available
    const scoreData = feedback ? calculateScore() : {
        score: 0,
        maxScore: 10,
        incorrectAnswers: 0,
        averageCertaintyCorrect: 0,
        averageCertaintyIncorrect: 0,
        slowness: 0,
        slownessText: "Normal",
        averageCertaintyCorrectText: "None",
        averageCertaintyIncorrectText: "None",
        uniqueWeaknesses: [],
        certaintyCounts: { correct: { high: 0, medium: 0, low: 0 }, incorrect: { high: 0, medium: 0, low: 0 } },
        attempts: 0,
        qcmIsValid: 0
    };

  // Fonction pour faire défiler vers le haut de la page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour envoyer les erreurs au backend
  const sendErrorToBackend = async (error, context) => {
        try {
            const errorData = {
                user_id: sessionContext?.user || 'anonymous_user',
                exam_id: sessionContext?.exam || 'unknown',
                session_id: sessionContext?.sessionId || Date.now().toString(),
                error_message: error.message || error.toString(),
                error_stack: error.stack || '',
                context: context,
                timestamp: new Date().toISOString(),
                user_agent: navigator.userAgent,
                url: window.location.href
            };
            
            console.log('🚨 Envoi erreur au backend:', errorData);
            
            await fetch(`${SCORING_API_BASE_URL}/api/log_error`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(errorData)
            });
        } catch (logError) {
            console.error('❌ Impossible d\'envoyer l\'erreur au backend:', logError);
        }
    };

    // Fonction pour envoyer qcm_multi_response_score au backend
    const submitQcmMultiResponseScore = async (qcmMultiResponseData) => {
        try {
            console.log('🔍 Envoi qcm_multi_response_score avec:', qcmMultiResponseData);
            console.log('🔍 URL utilisée:', `${SCORING_API_BASE_URL}/api/qcm_multi_response_score`);
            console.log('🔍 SCORING_API_BASE_URL:', SCORING_API_BASE_URL);
            
            const response = await fetch(`${SCORING_API_BASE_URL}/api/qcm_multi_response_score`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(qcmMultiResponseData)
            });
            
            console.log('🔍 Réponse reçue:', response.status, response.statusText);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Erreur HTTP:', response.status, errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            
            const result = await response.json();
            console.log('✅ qcm_multi_response_score envoyé avec succès:', result);
            return result;
        } catch (error) {
            console.error('❌ Erreur lors de l\'envoi de qcm_multi_response_score:', error);
            console.error('❌ Détails de l\'erreur:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            
            // Envoyer l'erreur au backend - COMMENTÉ pour éviter le cercle vicieux avec rate limit
            // await sendErrorToBackend(error, 'submitQcmMultiResponseScore');
            
            // Re-throw pour que l'appelant puisse gérer l'erreur
            throw error;
        }
    };


    // Fonction pour calculer et envoyer les métriques
    const calculateAndSendMetrics = async (qcmMultiResponseData) => {
        try {
            const requestData = {
                user_id: sessionContext?.user || 'anonymous_user',
                exam_id: sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID,
                session_id: sessionContext?.sessionId || Date.now().toString(),
                qcm_multi_response_score: qcmMultiResponseData
            };
            
            console.log('🔍 Calcul métriques avec:', requestData);
            console.log('🔍 URL utilisée:', `${SCORING_API_BASE_URL}/api/calculate_score_metrics`);
            
            // Envoyer les métriques en arrière-plan (async)
            const response = await fetch(`${SCORING_API_BASE_URL}/api/calculate_score_metrics`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(requestData)
            });
            
            console.log('🔍 Réponse calcul métriques reçue:', response.status, response.statusText);
            
            if (response.ok) {
                const metricsResult = await response.json();
                console.log('✅ Métriques calculées et stockées:', metricsResult);
                // Les métriques seront automatiquement rafraîchies par le composant UserMetricsPanel
            } else {
                const errorText = await response.text();
                console.error('❌ Erreur HTTP lors du calcul des métriques:', response.status, errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
        } catch (error) {
            console.error('❌ Erreur lors du calcul des métriques:', error);
            console.error('❌ Détails de l\'erreur:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            
            // Envoyer l'erreur au backend - COMMENTÉ pour éviter le cercle vicieux avec rate limit
            // await sendErrorToBackend(error, 'calculateAndSendMetrics');
        }
    };

    // One-time post to backend with QCM score when solution is visible
    useEffect(() => {
        if (!feedback) return;
        if (scoreSubmittedRef.current) return;
        
        // Lancer les tests de validation quand le feedback est disponible
        if (optionScoresArray.length > 0 && optionCertaintyArray.length > 0) {
            runScoreValidationTests();
        }
        
        // ensure stable hook order: do not early-return component above
        const qcmId = currentQCM?.qcm_id || '';
        const { domain, task, topic, keywords } = getQcmCanonicalMetadata(currentQCM);

        // Payload qcm_multi_response_score avec questionType discriminant
        const qcmType = isBoosterActive ? 'qcmBooster' : 'qcm';
        
        const qcmMultiResponseData = {
            user_id: sessionContext?.user || 'anonymous_user',
            exam_id: sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID,
            session_id: sessionContext?.sessionId || Date.now().toString(),
            qcm_id: qcmId,
            questionType: qcmType, // NOUVEAU: Discriminant pour le scoring
            domain: domain,
            task: task,
            topic: topic,
            keywords: keywords,
            timestamp: new Date().toISOString(),
            total_elapsed_time: Math.max(...optionScoresArray.map(opt => opt.elapsed_time), 0),
            total_score: scoreData.score,
            max_score: scoreData.maxScore,
            options: optionScoresArray.map(opt => ({
                option: opt.option,
                is_correct: opt.is_correct,
                certainty: opt.certainty,
                elapsed_time: opt.elapsed_time,
                unitary_score: opt.unitaryScore,
                score_breakdown: opt.scoreBreakdown,
                domain: opt.domain,
                task: opt.task,
                topic: opt.topic,
                keywords: opt.keywords,
                qcm_id: opt.qcm_id || qcmId,
            }))
        };
        
        // CORRECTION: Décommenter l'appel pour envoyer le score au backend
        submitQcmMultiResponseScore(qcmMultiResponseData);
        
        // Le calcul des métriques est maintenant fait côté backend, pas besoin d'appeler calculateAndSendMetrics ici
        
        scoreSubmittedRef.current = true;
    }, [feedback, scoreData, currentQCM, optionScoresArray, optionCertaintyArray, sessionContext]);

    // Reset submission flag when QCM changes
    useEffect(() => {
        scoreSubmittedRef.current = false;
        makeupDecrementAppliedRef.current = false;
    }, [currentQCM?.qcm_id]);

    // Keep weakness profile fresh after each answered question.
    useEffect(() => {
        if (!feedback) return;
        setMakeupVersion((v) => v + 1);
    }, [feedback, currentQCM?.qcm_id]);

    // Check if correct selection(s) are made
    // - For multi-answer: all correct answers must be selected
    // - For single-answer: the selected option must be exactly the correct one
    const allCorrectSelected = correctAnswers.length > 1 
        ? correctAnswers.every(ca => selectedAnswers.has(ca.answer))
        : (selectedOption !== null && correctAnswers.some(ca => ca.answer === selectedOption));

    // Review / renforcement : décrémenter make-up_questions_nb une fois la question entièrement réussie
    useEffect(() => {
        if (!feedback || !allCorrectSelected) return;
        if (makeupDecrementAppliedRef.current) return;
        makeupDecrementAppliedRef.current = true;
        const userId = sessionContext?.user || 'anonymous_user';
        const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        const sid = sessionContext?.sessionId;
        const meta = getQcmCanonicalMetadata(currentQCM);
        const scoped = decrementMakeupSessionForCompletedQcm(userId, exam, sid, {
            task: meta.task,
            keywords: meta.keywords,
        });
        if (!scoped.applied && isReinforcementWeaknessActive && activeReinforcementSessionId) {
            const remaining = decrementMakeupSessionNb(userId, activeReinforcementSessionId);
            if (remaining === null) {
                setActiveReinforcementSessionId(null);
            }
        }
        setMakeupVersion((v) => v + 1);
    }, [
        feedback,
        allCorrectSelected,
        isReinforcementWeaknessActive,
        activeReinforcementSessionId,
        sessionContext?.user,
        sessionContext?.exam,
        sessionContext?.sessionId,
        currentQCM,
    ]);

    // Freeze timer when solution becomes visible
    const isSolutionVisible = feedback && allCorrectSelected;
    useEffect(() => {
        if (isSolutionVisible && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, [isSolutionVisible]);

    // Auto-scroll to top when solution UI appears
    useEffect(() => {
        if (isSolutionVisible) {
            // Scroller immédiatement et aussi après un court délai pour être sûr
            const scrollToTop = () => {
                if (solutionContainerRef.current) {
                    solutionContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
                }
            };
            scrollToTop();
            // Scroll aussi après un court délai au cas où le DOM n'est pas encore prêt
            const timeoutId = setTimeout(scrollToTop, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [isSolutionVisible, feedback, allCorrectSelected]);

    // Dashboarding (async côté API) : recalcul quand l’écran résultats s’affiche
    useEffect(() => {
        if (!isSolutionVisible) return;
        const uid = sessionContext?.user || 'anonymous_user';
        const exam = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
        const makeup = loadMakeupSessionsForExamSession(uid, exam, sessionContext?.sessionId);
        triggerDashboardCompute(uid, exam, makeup, sessionContext?.sessionId).catch(() => {});
    }, [isSolutionVisible, sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    // Auto-scroll to top when changing QCM (new question)
    useEffect(() => {
        setStrategyInfoExpanded(false);
    }, [currentQCM?.qcm_id, currentQCMIndex, isSolutionVisible]);

    useEffect(() => {
        if (feedback && allCorrectSelected) {
            setStrategyInfoExpanded(false);
        }
    }, [feedback, allCorrectSelected]);

    useEffect(() => {
        if (currentQCM) {
            // Scroller le conteneur du QCM, pas window
            if (qcmContainerRef.current) {
                qcmContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
            }
            if (solutionContainerRef.current) {
                solutionContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
            }
        }
    }, [currentQCMIndex, currentQCM?.id]);
    const renderRevisionChromeStack = () => (
        <>
            {/* Revision strategy funnel (spec strategy_apprentissage) */}
            {isStrategyPanelOpen && (
                <div
                    className="fixed inset-0 z-[10080] flex flex-col justify-end bg-black/70"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="revision-strategy-title"
                    onClick={() => setIsStrategyPanelOpen(false)}
                >
                    <div
                        className="bg-gray-900 border-t border-gray-600 rounded-t-2xl max-h-[88vh] overflow-y-auto p-4 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h2 id="revision-strategy-title" className="text-lg font-semibold text-white">
                                Revision strategy
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsStrategyPanelOpen(false)}
                                className="text-gray-400 hover:text-white p-2"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-4">
                            {[
                                { id: 'none', label: 'Random' },
                                { id: 'A', label: 'Global weakness' },
                                { id: 'B', label: 'focused on specific weaknesses' },
                                { id: 'C', label: 'by domain' },
                                { id: 'D', label: 'by sub-domain' },
                                { id: 'E', label: 'Under-practiced topics' },
                                { id: 'F', label: 'by selected qcm' },
                                { id: 'G', label: 'by selected Flashcard' },
                                { id: 'H', label: 'by key words' },
                                { id: 'I', label: 'Unseen QCM' },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => {
                                        setStrategySubTab(tab.id);
                                        if (tab.id === 'none') {
                                            persistRevisionStrategy({
                                                ...DEFAULT_REVISION_STRATEGY,
                                            });
                                            setIsReinforcementWeaknessActive(false);
                                            setActiveReinforcementSessionId(null);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        } else if (tab.id === 'A') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'weakness_all',
                                                granularKeys: [],
                                                revisionTags: [],
                                                domainSelection: [],
                                                taskSelection: [],
                                            }));
                                            setIsReinforcementWeaknessActive(true);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                true
                                            );
                                        } else if (tab.id === 'E') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'left_topics',
                                                taskSelection: [],
                                                revisionTags: [],
                                                granularKeys: [],
                                                domainSelection: [],
                                            }));
                                            setIsReinforcementWeaknessActive(false);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        } else if (tab.id === 'F') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'selected_qcm',
                                                selectedItemIds: selectedQcmItems.map((x) => String(x.item_id || x.id || '')),
                                                studyMode: 'qcm',
                                            }));
                                            setShowSelectedQcmModal(true);
                                        } else if (tab.id === 'G') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'selected_flashcard',
                                                selectedItemIds: selectedFlashItems.map((x) => String(x.item_id || x.id || '')),
                                                studyMode: 'flashcard',
                                            }));
                                            setShowSelectedFlashModal(true);
                                        } else if (tab.id === 'H') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'keywords_fuzzy',
                                                taskSelection: [],
                                                revisionTags: [],
                                                granularKeys: [],
                                                domainSelection: [],
                                                selectedItemIds: [],
                                            }));
                                            setIsReinforcementWeaknessActive(false);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        } else if (tab.id === 'I') {
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'unseen_qcm',
                                                taskSelection: [],
                                                revisionTags: [],
                                                granularKeys: [],
                                                domainSelection: [],
                                                selectedItemIds: [],
                                            }));
                                            setIsReinforcementWeaknessActive(false);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        }
                                    }}
                                    className={`inline-flex h-8 shrink-0 items-center justify-center rounded-lg border px-2.5 text-xs font-medium transition-colors ${
                                        strategySubTab === tab.id
                                            ? 'border-teal-500/55 bg-teal-950/45 text-teal-100'
                                            : 'border-gray-700/90 bg-gray-900/70 text-gray-400 hover:border-gray-600 hover:text-gray-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {strategySubTab === 'none' && (
                            <p className="text-sm text-gray-400 mb-4">
                                Random flow — no revision filter. Use the other tabs to target weaknesses, domains, or sub-domains.
                            </p>
                        )}

                        {strategySubTab === 'A' && (
                            <div className="space-y-3 mb-4">
                                <p className="text-sm text-gray-400">
                                    Topics are selected based on your identified weaknesses. Note that these
                                    areas are weighted: the system prioritizes more significant gaps and
                                    re-evaluates your profile after each answer.
                                </p>
                            </div>
                        )}

                        {strategySubTab === 'B' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400 mb-2">
                                    Your weaknesses are displayed with keywords and weakness levels. Select
                                    the weaknesses you want to focus on.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowGranularModal(true)}
                                    className="w-full py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm"
                                >
                                    Choose weaknesses ({revisionStrategy.granularKeys.length} selected)
                                </button>
                            </div>
                        )}

                        {strategySubTab === 'C' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">Strict filter on: domain</p>
                                <button
                                    type="button"
                                    onClick={() => setShowDomainModal(true)}
                                    className="w-full py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm"
                                >
                                    Choose domains ({revisionStrategy.domainSelection.length} selected)
                                </button>
                            </div>
                        )}

                        {strategySubTab === 'D' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">Strict filter on: sub-domain</p>
                                <button
                                    type="button"
                                    onClick={() => setShowTaskModal(true)}
                                    className="w-full py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm"
                                >
                                    Choose sub-domains ({revisionStrategy.taskSelection.length} selected)
                                </button>
                            </div>
                        )}

                        {strategySubTab === 'E' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">
                                    Under-practiced topics are selected automatically from exam sub-domains with the lowest asked count.
                                </p>
                                <p className="text-xs text-gray-500">
                                    No manual selection is required in this mode.
                                </p>
                            </div>
                        )}
                        {strategySubTab === 'F' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">
                                    Practice from learner-selected QCM only.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowSelectedQcmModal(true)}
                                    className="w-full py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm"
                                >
                                    Choose selected QCM ({(revisionStrategy.selectedItemIds || []).length} selected)
                                </button>
                            </div>
                        )}
                        {strategySubTab === 'G' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">
                                    Practice from learner-selected Flashcards only.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowSelectedFlashModal(true)}
                                    className="w-full py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm"
                                >
                                    Choose selected Flashcards ({(revisionStrategy.selectedItemIds || []).length} selected)
                                </button>
                            </div>
                        )}
                        {strategySubTab === 'H' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">
                                    Enter one or more keywords. This uses the same fuzzy search as the QCM search engine.
                                </p>
                                <textarea
                                    value={keywordInput}
                                    onChange={(e) => setKeywordInput(e.target.value)}
                                    rows={3}
                                    placeholder="ex: vpc, s3 policy, iam role"
                                    className="w-full rounded-lg bg-gray-800 border border-gray-600 text-gray-100 text-sm p-2 outline-none focus:border-teal-500/60"
                                />
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = parseKeywordSelection(keywordInput);
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: next.length ? 'keywords_fuzzy' : 'none',
                                                keywordSelection: next,
                                                taskSelection: [],
                                                revisionTags: [],
                                                granularKeys: [],
                                                domainSelection: [],
                                            }));
                                            setIsReinforcementWeaknessActive(false);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        }}
                                        className="flex-1 py-2 rounded-lg bg-teal-950/40 border border-teal-600/50 text-teal-100 text-sm"
                                    >
                                        Apply keywords
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setKeywordInput('');
                                            persistRevisionStrategy((p) => ({
                                                ...p,
                                                filterType: 'none',
                                                keywordSelection: [],
                                            }));
                                        }}
                                        className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-300 text-sm"
                                    >
                                        Clear
                                    </button>
                                </div>
                                {!!(revisionStrategy.keywordSelection || []).length && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {(revisionStrategy.keywordSelection || []).map((kw) => (
                                            <span
                                                key={`kw-${kw}`}
                                                className="rounded bg-gray-800/80 px-2 py-0.5 text-[11px] text-gray-300 border border-gray-700"
                                            >
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {strategySubTab === 'I' && (
                            <div className="mb-4 space-y-2">
                                <p className="text-sm text-gray-400">
                                    Serve only questions you have not answered yet in this exam.
                                </p>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => setIsStrategyPanelOpen(false)}
                            className="w-full py-3 rounded-lg border border-teal-600/50 bg-teal-950/40 text-teal-100 font-medium hover:bg-teal-950/55"
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}

            {showDomainModal && (
                <div
                    className="fixed inset-0 z-[10090] bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setShowDomainModal(false)}
                >
                    <div
                        className="bg-gray-900 border border-gray-600 rounded-xl max-h-[70vh] w-full max-w-md overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-3 border-b border-gray-700 text-white font-medium">Domains</div>
                        <div className="overflow-y-auto flex-1 p-2 space-y-1">
                            <button
                                type="button"
                                onClick={() => {
                                    persistRevisionStrategy((prev) => ({
                                        ...prev,
                                        domainSelection: ['All'],
                                        taskSelection: [],
                                        granularKeys: [],
                                        revisionTags: [],
                                        filterType: 'manual_domain',
                                    }));
                                    setIsReinforcementWeaknessActive(false);
                                    savePreferences(
                                        isBoosterActive,
                                        isTargetWeaknessActive,
                                        false
                                    );
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                    revisionStrategy.domainSelection.includes('All')
                                        ? 'border-teal-500/50 bg-teal-950/40 text-teal-100'
                                        : 'border-transparent bg-gray-800 text-gray-200'
                                }`}
                            >
                                All
                            </button>
                            {examCatalog.domains.length === 0 && (
                                <p className="text-gray-500 text-sm px-2 py-2">
                                    No domains in catalog. Verify exam id matches Mongo (exam_id / exam) and
                                    metadata.Domain or root domain/theme.
                                </p>
                            )}
                            {examCatalog.domains.map((d) => (
                                <button
                                    key={d.name}
                                    type="button"
                                    onClick={() => {
                                        persistRevisionStrategy((prev) => {
                                            let next = [...prev.domainSelection].filter((x) => x !== 'All');
                                            const catalogName = String(d.name).trim();
                                            const idx = next.findIndex((x) =>
                                                domainSelectionIncludesName([x], catalogName)
                                            );
                                            if (idx >= 0) {
                                                next = next.filter((_, j) => j !== idx);
                                            } else {
                                                next.push(catalogName);
                                            }
                                            return {
                                                ...prev,
                                                domainSelection: next,
                                                taskSelection: [],
                                                granularKeys: [],
                                                revisionTags: [],
                                                filterType: next.length ? 'manual_domain' : 'none',
                                            };
                                        });
                                        setIsReinforcementWeaknessActive(false);
                                        savePreferences(
                                            isBoosterActive,
                                            isTargetWeaknessActive,
                                            false
                                        );
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                        domainSelectionIncludesName(revisionStrategy.domainSelection, d.name)
                                            ? 'border-teal-500/50 bg-teal-950/40 text-teal-100'
                                            : 'border-transparent bg-gray-800 text-gray-200'
                                    }`}
                                >
                                    {d.name}{' '}
                                    <span className="text-gray-500 text-xs">({d.weight_percent}%)</span>
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowDomainModal(false)}
                            className="p-3 border-t border-gray-700 text-orange-400 font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showTaskModal && (
                <div
                    className="fixed inset-0 z-[10090] bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setShowTaskModal(false)}
                >
                    <div
                        className="bg-gray-900 border border-gray-600 rounded-xl max-h-[70vh] w-full max-w-md overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-3 border-b border-gray-700 text-white font-medium">Sub-domains</div>
                        <div className="overflow-y-auto flex-1 p-2 space-y-1">
                            {(examCatalog.subdomains?.length ? examCatalog.subdomains : examCatalog.tasks).length ===
                                0 && (
                                <p className="text-gray-500 text-sm px-2 py-4">
                                    No sub-domains in catalog for this exam. Check exam id or Mongo metadata
                                    (Task / Sub_domain).
                                </p>
                            )}
                            {(examCatalog.subdomains?.length ? examCatalog.subdomains : examCatalog.tasks).map((t) => {
                                const sub = t.sub_domain || t.task;
                                return (
                                <button
                                    key={`${sub}|${t.parent_domain}`}
                                    type="button"
                                    onClick={() => {
                                        persistRevisionStrategy((prev) => {
                                            let next = [...prev.taskSelection];
                                            const subKey = String(sub).trim();
                                            const idx = next.findIndex((x) =>
                                                taskSelectionIncludesName([x], subKey)
                                            );
                                            if (idx >= 0) {
                                                next = next.filter((_, j) => j !== idx);
                                            } else {
                                                next.push(subKey);
                                            }
                                            return {
                                                ...prev,
                                                taskSelection: next,
                                                domainSelection: [],
                                                granularKeys: [],
                                                revisionTags: [],
                                                filterType: next.length ? 'manual_task' : 'none',
                                            };
                                        });
                                        setIsReinforcementWeaknessActive(false);
                                        savePreferences(
                                            isBoosterActive,
                                            isTargetWeaknessActive,
                                            false
                                        );
                                    }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                        taskSelectionIncludesName(revisionStrategy.taskSelection, sub)
                                            ? 'border-teal-500/50 bg-teal-950/40 text-teal-100'
                                            : 'border-transparent bg-gray-800 text-gray-200'
                                    }`}
                                >
                                    <span className="font-medium">{sub}</span>
                                    <span className="text-gray-500"> | {t.parent_domain || '—'} | </span>
                                    <span className="text-gray-400">{t.weight_percent}%</span>
                                </button>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowTaskModal(false)}
                            className="p-3 border-t border-gray-700 text-orange-400 font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showGranularModal && (
                <div
                    className="fixed inset-0 z-[10090] bg-black/90 flex items-center justify-center p-2"
                    onClick={() => setShowGranularModal(false)}
                >
                    <div
                        className="bg-gray-900 border border-gray-600 rounded-xl h-[92vh] w-full max-w-2xl overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-3 border-b border-gray-700 text-white font-medium">
                            Focused weaknesses
                        </div>
                        <div className="overflow-y-auto flex-1 p-3 space-y-3">
                            {makeupConcepts.length === 0 && (
                                <p className="text-gray-500 text-sm px-2 py-4">
                                    No active make-up sessions yet.
                                </p>
                            )}
                            {makeupConcepts.map((row) => {
                                const selected = revisionStrategy.granularKeys.includes(row.key);
                                return (
                                    <button
                                        key={row.key}
                                        type="button"
                                        onClick={() => {
                                            const set = new Set(revisionStrategy.granularKeys);
                                            if (set.has(row.key)) set.delete(row.key);
                                            else set.add(row.key);
                                            const keys = [...set];
                                            persistRevisionStrategy((prev) => ({
                                                ...prev,
                                                granularKeys: keys,
                                                filterType: keys.length > 0 ? 'weakness_granular' : 'none',
                                                domainSelection: [],
                                                taskSelection: [],
                                            }));
                                            setIsReinforcementWeaknessActive(false);
                                            savePreferences(
                                                isBoosterActive,
                                                isTargetWeaknessActive,
                                                false
                                            );
                                        }}
                                        className={`w-full text-left rounded-lg border px-3 py-3 transition-colors ${
                                            selected
                                                ? 'border-teal-500/60 bg-teal-950/35'
                                                : 'border-gray-700 bg-gray-800/60'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0 text-[11px] text-gray-400 truncate">
                                                {row.domain || 'Domain'}
                                            </div>
                                            <div className="shrink-0 text-[11px] text-gray-500">
                                                {row.sessionCount || 1} session(s)
                                            </div>
                                        </div>
                                        <div className="mt-1 text-xs text-gray-200 line-clamp-2">
                                            {(row.keywords || []).join(' · ')}
                                        </div>
                                        <div className="mt-2 h-1.5 rounded bg-gray-700 overflow-hidden">
                                            <div
                                                className="h-full bg-teal-500/80"
                                                style={{
                                                    width: `${Math.max(
                                                        8,
                                                        Math.min(100, ((row.avgScore || 0) / 5) * 100)
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="mt-1 text-[11px] text-gray-500">
                                            weakness level: {row.avgScore || 0} / 5
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowGranularModal(false)}
                            className="p-3 border-t border-gray-700 text-orange-400 font-medium"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showSelectedQcmModal && (
                <div className="fixed inset-0 z-[10090] bg-black/90 flex items-center justify-center p-2" onClick={() => setShowSelectedQcmModal(false)}>
                    <div className="bg-gray-900 border border-gray-600 rounded-xl h-[86vh] w-full max-w-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 border-b border-gray-700 text-white font-medium">Selected QCM</div>
                        <div className="overflow-y-auto flex-1 p-3 space-y-2">
                            {selectedQcmItems.map((row) => {
                                const id = String(row.item_id || '');
                                const selected = (revisionStrategy.selectedItemIds || []).includes(id);
                                return (
                                    <button
                                        key={`sel-qcm-${id}`}
                                        type="button"
                                        onClick={() => persistRevisionStrategy((p) => {
                                            const cur = new Set(p.selectedItemIds || []);
                                            if (cur.has(id)) cur.delete(id); else cur.add(id);
                                            return { ...p, filterType: 'selected_qcm', studyMode: 'qcm', selectedItemIds: [...cur] };
                                        })}
                                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${selected ? 'border-teal-500/60 bg-teal-950/35 text-teal-100' : 'border-gray-700 bg-gray-800/60 text-gray-200'}`}
                                    >
                                        <div className="line-clamp-2">{row.label || id}</div>
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" onClick={() => setShowSelectedQcmModal(false)} className="p-3 border-t border-gray-700 text-orange-400 font-medium">Close</button>
                    </div>
                </div>
            )}

            {showSelectedFlashModal && (
                <div className="fixed inset-0 z-[10090] bg-black/90 flex items-center justify-center p-2" onClick={() => setShowSelectedFlashModal(false)}>
                    <div className="bg-gray-900 border border-gray-600 rounded-xl h-[86vh] w-full max-w-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 border-b border-gray-700 text-white font-medium">Selected Flashcards</div>
                        <div className="overflow-y-auto flex-1 p-3 space-y-2">
                            {selectedFlashItems.map((row) => {
                                const id = String(row.item_id || '');
                                const selected = (revisionStrategy.selectedItemIds || []).includes(id);
                                return (
                                    <button
                                        key={`sel-flash-${id}`}
                                        type="button"
                                        onClick={() => persistRevisionStrategy((p) => {
                                            const cur = new Set(p.selectedItemIds || []);
                                            if (cur.has(id)) cur.delete(id); else cur.add(id);
                                            return { ...p, filterType: 'selected_flashcard', studyMode: 'flashcard', selectedItemIds: [...cur] };
                                        })}
                                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${selected ? 'border-teal-500/60 bg-teal-950/35 text-teal-100' : 'border-gray-700 bg-gray-800/60 text-gray-200'}`}
                                    >
                                        <div className="line-clamp-2">{row.problem || row.label || id}</div>
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" onClick={() => setShowSelectedFlashModal(false)} className="p-3 border-t border-gray-700 text-orange-400 font-medium">Close</button>
                    </div>
                </div>
            )}

            {/* Hide local mobile menu and overlay when global menu is active */}
            {!window.__GLOBAL_MOBILE_MENU__ && (
                <>
            <MobileStudySideMenu
                hidden={isGlobalMenu}
                isFlashMode={isFlashMode}
                isMenuOpen={isMenuOpen}
                onMenuOpenChange={setIsMenuOpen}
                onMenuAction={handleMenuAction}
                onOpenQcmStudy={openQcmStudyFromRail}
                onOpenFlashcardStudy={openFlashcardStudyFromRail}
            />
            {/* Mobile search overlay (#5) - local overlay hidden when global menu is active */}
            {!isGlobalMenu && isSearchOpen && (
                        <div className="fixed inset-0 bg-black/70 z-[10070] flex items-center justify-center p-4">
                            <div className="w-full max-w-md bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                                    <div className="text-gray-200 font-semibold sr-only">Search</div>
                                    <button onClick={() => setIsSearchOpen(false)} className="text-gray-400 hover:text-white">
                                        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="p-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => persistStudyMode('qcm')}
                                            className={`rounded-lg border px-3 py-2 text-sm font-semibold transition inline-flex items-center justify-center gap-2 ${
                                                !isFlashMode
                                                    ? 'border-teal-500/80 bg-teal-600/90 text-white'
                                                    : 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
                                            }`}
                                        >
                                            <IconQcm className="w-4 h-4" />
                                            QCM
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => persistStudyMode('flashcard')}
                                            className={`rounded-lg border px-3 py-2 text-sm font-semibold transition inline-flex items-center justify-center gap-2 ${
                                                isFlashMode
                                                    ? 'border-teal-500/80 bg-teal-600/90 text-white'
                                                    : 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
                                            }`}
                                        >
                                            <IconFlashcard className="w-4 h-4" />
                                            Flash Card
                                        </button>
                                    </div>
                                    <div className="flex items-stretch overflow-hidden rounded-lg border border-slate-700 bg-slate-800 focus-within:border-teal-500">
                                        <svg
                                            className="w-6 h-6 shrink-0 self-center text-slate-300 ml-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                                        </svg>
                                        <input
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') performMobileSearch();
                                            }}
                                            placeholder={isFlashMode ? 'Search a Flash Card' : 'Search a QCM'}
                                            ref={searchInputRef}
                                            className="min-w-0 flex-1 px-3 py-2.5 bg-transparent text-white placeholder-slate-400 focus:outline-none text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={performMobileSearch}
                                            className="mr-1 shrink-0 self-stretch border-l border-slate-700 bg-slate-800 px-3 text-sm font-semibold text-teal-300 hover:bg-slate-700 hover:text-white inline-flex items-center"
                                        >
                                            Go
                                        </button>
                                    </div>
                                    {searchLoading && (
                                        <div className="text-center text-gray-400 text-sm">Searching...</div>
                                    )}
                                    {searchError && (
                                        <div className="text-center text-red-400 text-sm">{searchError}</div>
                                    )}
                                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-800">
                                        {searchResults.map((r) => (
                                            <button key={r.id} onClick={() => handleSelectSearchResult(r)} className="w-full text-left p-3 hover:bg-gray-800">
                                                <div className="text-gray-100 text-sm mb-1 line-clamp-2">{r.question || 'Question'}</div>
                                                {(r.domain || r.task) && (
                                                    <div className="text-gray-400 text-xs">{[r.domain, r.task].filter(Boolean).join(' | ')}</div>
                                                )}
                                                {r.keywords && r.keywords.length > 0 && (
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        {r.keywords.slice(0, 4).map((k, idx) => (
                                                            <span key={idx} className="text-[10px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{k}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                        {!searchLoading && searchResults.length === 0 && !searchError && (
                                            <div className="text-center text-gray-500 text-sm p-3">No results</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
            {!isGlobalMenu && isSessionPanelOpen && (
                <div className="fixed inset-0 bg-black/70 z-[10075] flex items-center justify-center p-3">
                    <div className="w-full max-w-md max-h-[90vh] overflow-hidden bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                            <h3 className="text-sm font-semibold text-cyan-100">Session manager</h3>
                            <button onClick={() => setIsSessionPanelOpen(false)} className="text-slate-400 hover:text-white">
                                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-4 py-3 border-b border-slate-700 space-y-2">
                            <div className="text-[11px] text-slate-400">
                                New session label (max 12 chars)
                            </div>
                            <div className="flex gap-2">
                                <input
                                    value={sessionShortLabel}
                                    maxLength={12}
                                    onChange={(e) => setSessionShortLabel(String(e.target.value || '').slice(0, 12))}
                                    placeholder="S01AIP"
                                    className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                                />
                                <button
                                    type="button"
                                    onClick={createNewSession}
                                    className="rounded-md bg-teal-600 hover:bg-teal-500 px-3 py-2 text-sm font-semibold text-white"
                                >
                                    Create
                                </button>
                            </div>
                            {sessionPanelError && <div className="text-xs text-red-400">{sessionPanelError}</div>}
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-2">
                            {sessionPanelLoading && (
                                <div className="text-center text-xs text-slate-400 py-3">Loading sessions...</div>
                            )}
                            {!sessionPanelLoading && sessionSummaries.length === 0 && (
                                <div className="text-center text-xs text-slate-500 py-3">No session yet for this exam.</div>
                            )}
                            {sessionSummaries.map((s) => {
                                const isExpanded = expandedSessionId === s.sessionId;
                                const titleDate = s.lastQcmAt || s.updatedAt || s.createdAt;
                                const dateText = titleDate ? new Date(titleDate).toLocaleString() : 'No activity';
                                return (
                                    <div key={s.sessionId} className="rounded-lg border border-slate-700 bg-slate-800/80">
                                        <button
                                            type="button"
                                            onClick={() => setExpandedSessionId(isExpanded ? null : s.sessionId)}
                                            className="w-full px-3 py-2 flex items-center justify-between text-left"
                                        >
                                            <div className="text-[11px] text-slate-200 truncate">{dateText}</div>
                                            <div className="text-xs font-semibold text-teal-300">{Number(s.validatedPct || 0).toFixed(1)}%</div>
                                        </button>
                                        {isExpanded && (
                                            <div className="px-3 pb-3 space-y-1 text-[11px] text-slate-300 border-t border-slate-700">
                                                <div className="pt-2"><span className="text-slate-500">Id:</span> {s.sessionId}</div>
                                                <div><span className="text-slate-500">Label:</span> {s.labelShort || '-'}</div>
                                                <div><span className="text-slate-500">Last QCM:</span> {s.lastQcmAt ? new Date(s.lastQcmAt).toLocaleString() : '-'}</div>
                                                <div><span className="text-slate-500"># QCM:</span> {Number(s.qcmCount || 0)}</div>
                                                <div><span className="text-slate-500">% validated:</span> {Number(s.validatedPct || 0).toFixed(1)}%</div>
                                                <div><span className="text-slate-500">Make-up questions:</span> {Number(s.makeupQuestionsCount || 0)}</div>
                                                <div className="pt-2 flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => activateSession(s.sessionId)}
                                                        className={`rounded px-2 py-1 text-[11px] font-semibold ${
                                                            s.isActive
                                                                ? 'bg-emerald-700 text-white'
                                                                : 'bg-slate-700 hover:bg-slate-600 text-slate-100'
                                                        }`}
                                                    >
                                                        {s.isActive ? 'Active' : 'Activate'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteSessionWithData(s.sessionId)}
                                                        className="rounded px-2 py-1 text-[11px] font-semibold bg-red-700 hover:bg-red-600 text-white"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {!isGlobalMenu && openQuestionPanel && (
                <MobileOpenQuestionScreen
                    onClose={() => setOpenQuestionPanel(null)}
                    sessionContext={sessionContext}
                    currentQCM={currentQCM}
                    initialTab={openQuestionPanel.initialTab}
                />
            )}
                </>
            )}
        </>
    );


    // Si feedback disponible ET toutes les bonnes réponses sont sélectionnées, afficher l'UI dédiée pour la bonne réponse
    if (!isFlashMode && feedback && allCorrectSelected) {
        return (
            <div 
                ref={solutionContainerRef}
                className="mobile-qcm-container"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e7eb',
                    WebkitTextFillColor: '#e5e7eb',
                    zIndex: 9999,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y'
                }}
            >
                {/* Container scrollable interne */}
                <div 
                    className="mobile-scrollable-content"
                    style={{ 
                        minHeight: '100vh',
                        padding: '16px',
                        paddingBottom: '80px', // Plus d'espace pour le bouton
                        color: '#e5e7eb',
                    }}
                >
                    {/* Nom de l'examen en haut à gauche - Solution UI */}
                    <div className="fixed top-4 left-4 z-50">
                        <div className="text-sm text-gray-400 font-medium bg-black/20 px-3 py-1 rounded-lg backdrop-blur-sm border border-gray-700/30 shadow-lg">
                            {(() => {
                                const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
                                // Convertir l'ID en nom lisible
                                return examId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            })()}
                        </div>
                    </div>

                    <div className="fixed top-4 right-4 z-50 text-right">
                        <div className="text-sm">
                            <span
                                className={`${
                                    displayTime < (maxTime || 300) * 0.9
                                        ? 'text-green-400'
                                        : displayTime < (maxTime || 300)
                                          ? 'text-orange-400'
                                          : 'text-red-400'
                                }`}
                            >
                                {Math.floor(displayTime / 60)}:
                                {(displayTime % 60).toString().padStart(2, '0')}
                            </span>
                            <span className="text-blue-400">
                                {' '}
                                / {Math.floor((maxTime || 300) / 60)}:
                                {((maxTime || 300) % 60).toString().padStart(2, '0')}
                            </span>
                        </div>
                    </div>

                    <div className="h-10 mb-2"></div>

                    <RevisionStrategyInfoBar
                        revisionStrategy={revisionStrategy}
                        isReinforcementWeaknessActive={isReinforcementWeaknessActive}
                        isTargetWeaknessActive={isTargetWeaknessActive}
                        currentQCM={currentQCM}
                        examCatalog={examCatalog}
                        onOpenStrategyModal={() => setIsStrategyPanelOpen(true)}
                        expanded={false}
                        onToggleExpanded={() => {}}
                        variant="results"
                        resultsKeywords={lastGranularSearchKeywords}
                    />

                    {/* Section Validation Binaire avec Score */}
                    <div className={`${scoreData.qcmIsValid === 1 ? 'bg-green-900/20 border-green-500/30' : 'bg-orange-900/20 border-orange-500/30'} border rounded-lg p-6 mb-6`}>
                        {scoreData.qcmIsValid === 1 ? (
                            <div className="text-center">
                                <div className="mb-4">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full border-2 border-green-500/50 mb-3">
                                        <svg className="w-8 h-8 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-green-400 mb-2">Excellent!</h3>
                                <p className="text-green-300 mb-3">You answered correctly!</p>
                                <div className="text-3xl font-bold text-green-400 mb-2">{scoreData.score}/{scoreData.maxScore}</div>
                                <p className="text-sm text-gray-400">QCM Correction</p>
                                <div className="mt-2">
                                    <svg className="w-4 h-4 text-gray-400 animate-pulse mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                    </svg>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="mb-4">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/20 rounded-full border-2 border-orange-500/50 mb-3">
                                        <svg className="w-8 h-8 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-orange-400 mb-2">Keep Going!</h3>
                                <p className="text-orange-300 mb-3">Each attempt brings you closer to success!</p>
                                <div className="text-3xl font-bold text-orange-400 mb-2">{scoreData.score}/{scoreData.maxScore}</div>
                                <p className="text-sm text-gray-400">QCM Correction</p>
                                <div className="mt-2">
                                    <svg className="w-4 h-4 text-gray-400 animate-pulse mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tips Section */}
                    {currentQCM?.tips && (() => {
                        // Normaliser tips : peut être string ou array selon l'examen
                        const tipsArray = Array.isArray(currentQCM.tips) 
                            ? currentQCM.tips 
                            : (typeof currentQCM.tips === 'string' && currentQCM.tips.trim() 
                                ? [currentQCM.tips] 
                                : []);
                        return tipsArray.length > 0 && (
                            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                                <h3 className="text-lg font-semibold text-blue-400 mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    Tips
                                </h3>
                                <div className="space-y-2">
                                    {tipsArray.map((tip, index) => (
                                        <div key={index} className="flex items-start gap-2">
                                            <div className="text-blue-400 mt-1">💡</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">{tip}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Common Errors Section */}
                    {currentQCM?.common_error && (() => {
                        // Normaliser common_error : peut être string ou array selon l'examen
                        const errorsArray = Array.isArray(currentQCM.common_error) 
                            ? currentQCM.common_error 
                            : (typeof currentQCM.common_error === 'string' && currentQCM.common_error.trim() 
                                ? [currentQCM.common_error] 
                                : []);
                        return errorsArray.length > 0 && (
                            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
                                <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    Common Errors
                                </h3>
                                <div className="space-y-2">
                                    {errorsArray.map((error, index) => (
                                        <div key={index} className="flex items-start gap-2">
                                            <div className="text-red-400 mt-1">⚠️</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">{error}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* QCM Scoring Details */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            QCM Details
                        </h3>
                        
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-center justify-between">
                                        <div className="text-gray-400">Speed</div>
                                            <div className={`${
                                    scoreData.speedText === 'Fast' ? 'text-green-400' :
                                    scoreData.speedText === 'Normal' ? 'text-green-400' :
                                    scoreData.speedText === 'Limit' ? 'text-yellow-400' :
                                    scoreData.speedText === 'Slow' ? 'text-orange-400' : 'text-red-400'
                                } font-bold`}>{scoreData.speedText} ({scoreData.speed}%)</div>
                                        </div>
                            <div className="border-t border-dotted border-gray-600"></div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-gray-400">Attempts</div>
                                        <div className="text-blue-400 font-bold">{scoreData.attempts}</div>
                                    </div>
                            <div className="border-t border-dotted border-gray-600"></div>
                            <div className="flex items-center justify-between">
                                <div className="text-gray-400">Certainty (correct)</div>
                                <div className="flex flex-wrap gap-1">
                                            {Object.entries(scoreData.certaintyCounts.correct).map(([level, count]) => (
                                                count > 0 && (
                                                    <span key={level} className={`px-2 py-0.5 rounded-full text-xs ${
                                                        level === 'high' ? 'bg-green-900 text-green-300' :
                                                        level === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                                                        'bg-red-900 text-red-300'
                                                    }`}>
                                                        {level}: {count}
                                                    </span>
                                                )
                                            ))}
                            </div>
                        </div>
                            <div className="border-t border-dotted border-gray-600"></div>
                            <div className="flex items-center justify-between">
                                <div className="text-gray-400">Certainty (incorrect)</div>
                                <div className="flex flex-wrap gap-1">
                                            {Object.entries(scoreData.certaintyCounts.incorrect).map(([level, count]) => (
                                                count > 0 && (
                                                    <span key={level} className={`px-2 py-0.5 rounded-full text-xs ${
                                                        level === 'high' ? 'bg-green-900 text-green-300' :
                                                        level === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                                                        'bg-red-900 text-red-300'
                                                    }`}>
                                                        {level}: {count}
                                                    </span>
                                                )
                                            ))}
                                        </div>
                                    </div>
                            <div className="border-t border-dotted border-gray-600"></div>
                                    {scoreData.uniqueWeaknesses && scoreData.uniqueWeaknesses.length > 0 && (
                                        <div>
                                            <div className="text-gray-400 mb-1">Weaknesses</div>
                                    <div className="flex flex-wrap gap-1">
                                                {scoreData.uniqueWeaknesses.map((weakness, index) => (
                                                    <span key={index} className="text-red-300 text-xs bg-red-900/20 px-2 py-0.5 rounded-full">
                                                        {weakness}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>

                    {/* User Metrics - Reusable Component */}
                    <UserMetricsPanel sessionContext={sessionContext} className="mb-6" />


                    {/* Question Rappel */}
                    <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                            Question
                        </h3>
                        <div className="text-gray-200">
                            <SectionContent content={currentQCM?.qcm?.question || ''} />
                        </div>
                    </div>

                    {/* Bonnes Réponses */}
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {correctAnswers.length > 1 ? 'Correct Answers' : 'Correct Answer'}
                        </h3>
                        
                        {correctAnswers.map((correctAnswer, index) => (
                            <div key={index} className="mb-4 last:mb-0">
                                <div className="bg-gray-800 rounded p-3 mb-3">
                                    <p className="text-gray-200 font-medium">
                                        {correctAnswer.answer}
                                    </p>
                                </div>

                                {correctAnswer.explanation && (
                                    <div>
                                        <h4 className="text-md font-semibold text-green-300 mb-2">
                                            Explanation:
                                        </h4>
                                        <div className="text-gray-200 leading-relaxed">
                                            <SectionContent content={correctAnswer.explanation} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Options Incorrectes */}
                    {currentQCM?.qcm?.distractors?.length > 0 && (
                        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
                            <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Why other options are incorrect
                            </h3>
                            
                            <div className="space-y-4">
                                {currentQCM?.qcm?.distractors?.map((distractor, index) => (
                                    <div key={index} className="bg-gray-800 rounded p-3">
                                        <div className="flex items-start gap-3 mb-2">
                                            <div className="mt-1">
                                                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-gray-100 font-medium">{distractor.answer}</div>
                                            </div>
                                        </div>
                                        {distractor.explanation && (
                                            <div className="text-gray-300 text-lg mt-2">
                                                <span className="font-semibold text-red-300 mr-2">Why it's incorrect:</span>
                                                <SectionContent content={distractor.explanation} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Bouton Next QCM */}
                    <div className="fixed bottom-4 left-4 right-4">
                        <button
                            type="button"
                            onClick={() => {
                                loadNextQCM();
                                // Scroller immédiatement en haut après le changement de QCM
                                setTimeout(() => {
                                    if (qcmContainerRef.current) {
                                        qcmContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
                                    }
                                }, 50);
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-lg"
                            style={{ minHeight: '56px' }}
                        >
                            Next QCM →
                        </button>
                    </div>
                </div>

                {renderRevisionChromeStack()}
            </div>
        );
    }

    /** Aucun QCM mais stratégie bloquante : garder bandeau stratégie + message, pas de carte question vide. */
    const emptyStrategyNoQuestion =
        (!currentQCM || !currentQCM.qcm) && qcmLoadError === NO_QUESTION_FOR_STRATEGY_MESSAGE;

    // Ne jamais afficher un QCM (qcm/qcmBooster) dans le layout flash — seule Tips réelle.
    const isRealFlashcardItem =
        String(currentQCM?.questionType || '').trim() === 'FlashCard_Tips' ||
        Boolean(String(currentQCM?.Tips?.ProblemToResolve || '').trim());

    const flashProblemText =
        String(currentQCM?.Tips?.ProblemToResolve || '').trim() || '—';
    const flashSolutionText =
        String(currentQCM?.Tips?.Solution || '').trim() || '—';
    const currentItemId = String(
        currentQCM?.qcm_id ||
        currentQCM?.qcm?._id ||
        currentQCM?.qcm?.id ||
        currentQCM?.FlashCard_Tips_id ||
        currentQCM?.Tips?.FlashCard_Tips_id ||
        ''
    );
    const pinnedIds = new Set((isFlashMode ? selectedFlashItems : selectedQcmItems).map((x) => String(x.item_id || x.id || '')));
    const isCurrentPinned = currentItemId ? pinnedIds.has(currentItemId) : false;

    if (
        !emptyStrategyNoQuestion &&
        isFlashMode &&
        isRealFlashcardItem &&
        currentQCM
    ) {
        return (
            <div
                ref={qcmContainerRef}
                className="mobile-qcm-container"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e7eb',
                    WebkitTextFillColor: '#e5e7eb',
                    zIndex: 9999,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y',
                }}
            >
                <div
                    className="mobile-scrollable-content"
                    style={{
                        minHeight: '100vh',
                        paddingTop: '16px',
                        paddingLeft: '16px',
                        paddingBottom: '120px',
                        paddingRight: '76px',
                        color: '#e5e7eb',
                    }}
                >
                    <div className="fixed top-4 left-4 z-50">
                        <div className="text-sm text-gray-400 font-medium bg-black/20 px-3 py-1 rounded-lg backdrop-blur-sm border border-gray-700/30 shadow-lg">
                            {(sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </div>
                    </div>
                    <div className="fixed top-4 right-4 z-50 text-right">
                        <div className="text-sm">
                            <span
                                className={`${
                                    displayTime < (maxTime || 300) * 0.9
                                        ? 'text-green-400'
                                        : displayTime < (maxTime || 300)
                                          ? 'text-orange-400'
                                          : 'text-red-400'
                                }`}
                            >
                                {Math.floor(displayTime / 60)}:
                                {(displayTime % 60).toString().padStart(2, '0')}
                            </span>
                            <span className="text-blue-400">
                                {' '}
                                / {Math.floor((maxTime || 300) / 60)}:
                                {((maxTime || 300) % 60).toString().padStart(2, '0')}
                            </span>
                        </div>
                    </div>
                    <div className="h-10 mb-2" />

                    <RevisionStrategyInfoBar
                        revisionStrategy={revisionStrategy}
                        isReinforcementWeaknessActive={isReinforcementWeaknessActive}
                        isTargetWeaknessActive={isTargetWeaknessActive}
                        currentQCM={currentQCM}
                        examCatalog={examCatalog}
                        onOpenStrategyModal={() => setIsStrategyPanelOpen(true)}
                        expanded={strategyInfoExpanded}
                        onToggleExpanded={() => setStrategyInfoExpanded((v) => !v)}
                        variant="question"
                        resultsKeywords={null}
                    />

                    <div className="flex justify-end items-center gap-2 mb-2">
                        <div
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${
                                currentAnsweredStatus.answeredInSession
                                    ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-200'
                                    : currentAnsweredStatus.answeredInExam
                                        ? 'border-sky-400/70 bg-sky-500/15 text-sky-200'
                                        : 'border-gray-500/55 bg-gray-700/30 text-gray-300'
                            }`}
                            title={
                                currentAnsweredStatus.loading
                                    ? 'Checking answer history...'
                                    : currentAnsweredStatus.answeredInSession
                                        ? `Done in this session (${currentAnsweredStatus.answeredSessionCount})`
                                        : currentAnsweredStatus.answeredInExam
                                            ? `Done in this exam (${currentAnsweredStatus.answeredExamCount})`
                                            : 'New in this exam'
                            }
                            aria-label={
                                currentAnsweredStatus.answeredInSession
                                    ? 'Done in this session'
                                    : currentAnsweredStatus.answeredInExam
                                        ? 'Done in this exam'
                                        : 'New question'
                            }
                        >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.8-6.5 9.5-6.5S21.5 12 21.5 12s-3.8 6.5-9.5 6.5S2.5 12 2.5 12z" />
                                <circle cx="12" cy="12" r="2.5" />
                            </svg>
                            <span>{currentAnsweredStatus.loading ? '...' : (currentAnsweredStatus.answeredInExam ? 'Seen' : 'New')}</span>
                        </div>
                        <button
                            type="button"
                            onClick={toggleCurrentSelectionPin}
                            className={`rounded-full border p-1.5 transition-colors ${
                                isCurrentPinned
                                    ? 'border-amber-400/90 bg-amber-500/25 text-amber-100 ring-2 ring-amber-400/70 shadow-sm shadow-amber-900/40'
                                    : 'border-gray-500/55 bg-gray-700/30 text-gray-300 hover:bg-gray-600/35'
                            }`}
                            title={isCurrentPinned ? 'Remove from favorites' : 'Add to favorites'}
                            aria-label={isCurrentPinned ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill={isCurrentPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8L3.6 9.7l5.8-.8L12 3.6z" />
                            </svg>
                        </button>
                    </div>

                    <div className="mobile-flash-card-wrap mt-3 mb-4 space-y-4">
                        {!flashCardFlipped && (
                            <p className="mobile-flash-tap-hint text-center text-sm text-amber-200/90 px-1">
                                Tap the card to reveal the solution.
                            </p>
                        )}
                        <button
                            type="button"
                            className={`mobile-flash-card-surface w-full text-left rounded-2xl border transition-shadow duration-200 ${
                                flashCardFlipped
                                    ? 'border-emerald-600/45 bg-gradient-to-br from-emerald-950/55 to-gray-900/95 shadow-lg shadow-emerald-900/25'
                                    : 'border-amber-500/40 bg-gradient-to-br from-amber-950/45 to-gray-900/95 shadow-lg shadow-amber-900/20 active:scale-[0.99]'
                            }`}
                            onClick={() => setFlashCardFlipped((v) => !v)}
                            aria-expanded={flashCardFlipped}
                        >
                            <div className="px-5 py-5">
                                <div className="mobile-flash-kicker text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">
                                    {flashCardFlipped ? 'Solution' : 'Question'}
                                </div>
                                {!flashCardFlipped ? (
                                    <div className="mobile-flash-problem-text text-[17px] leading-[1.55] text-gray-50 font-normal">
                                        <SectionContent content={flashProblemText} />
                                    </div>
                                ) : (
                                    <div className="mobile-flash-solution-text text-[17px] leading-[1.65] text-emerald-50 font-normal">
                                        <SectionContent content={flashSolutionText} />
                                    </div>
                                )}
                            </div>
                        </button>

                        {flashCardFlipped && (
                            <div className="mobile-flash-rating-block space-y-4 pt-1">
                                <p className="text-center text-sm leading-snug text-gray-300 px-2">
                                    How well did you understand this?
                                </p>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <button
                                        type="button"
                                        className="mobile-flash-rating-btn rounded-2xl border border-rose-600/55 bg-rose-950/40 px-4 py-4 text-[15px] font-semibold text-rose-50 hover:bg-rose-900/45 active:scale-[0.99] shadow-md shadow-rose-950/30"
                                        onClick={() => handleFlashComprehension(-2, 'Need quick review')}
                                    >
                                        Need a quick review
                                    </button>
                                    <button
                                        type="button"
                                        className="mobile-flash-rating-btn rounded-2xl border border-amber-600/50 bg-amber-950/40 px-4 py-4 text-[15px] font-semibold text-amber-50 hover:bg-amber-900/40 active:scale-[0.99] shadow-md shadow-amber-950/25"
                                        onClick={() => handleFlashComprehension(-1, 'Medium grasp')}
                                    >
                                        Medium grasp
                                    </button>
                                    <button
                                        type="button"
                                        className="mobile-flash-rating-btn rounded-2xl border border-emerald-600/50 bg-emerald-950/45 px-4 py-4 text-[15px] font-semibold text-emerald-50 hover:bg-emerald-900/40 active:scale-[0.99] shadow-md shadow-emerald-950/25"
                                        onClick={() => handleFlashComprehension(2, 'Mastered')}
                                    >
                                        Mastered
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => goFlashcardAt(flashcardIndex - 1)}
                            disabled={flashcardIndex <= 0}
                            className="px-3 py-2 rounded-lg border border-gray-600 text-sm text-gray-200 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <div className="text-xs text-gray-400">
                            Card {flashcardIds.length ? flashcardIndex + 1 : 1} / {flashcardIds.length || 1}
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                void advanceToNextFlashcard();
                            }}
                            disabled={loading}
                            className="px-3 py-2 rounded-lg border border-gray-600 text-sm text-gray-200 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>

                    {renderRevisionChromeStack()}
                </div>
            </div>
        );
    }

    // Mode flash actif mais item courant encore un QCM (bascule en cours) : ne pas afficher le QCM en layout flash.
    if (isFlashMode && !emptyStrategyNoQuestion && !isRealFlashcardItem) {
        return (
            <div
                ref={qcmContainerRef}
                className="mobile-qcm-container"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e7eb',
                    WebkitTextFillColor: '#e5e7eb',
                    zIndex: 9999,
                    overflowY: 'auto',
                }}
            >
                <div className="mobile-scrollable-content" style={{ minHeight: '100vh', padding: '24px 76px 120px 16px' }}>
                    <RevisionStrategyInfoBar
                        revisionStrategy={revisionStrategy}
                        isReinforcementWeaknessActive={isReinforcementWeaknessActive}
                        isTargetWeaknessActive={isTargetWeaknessActive}
                        currentQCM={currentQCM}
                        examCatalog={examCatalog}
                        onOpenStrategyModal={() => setIsStrategyPanelOpen(true)}
                        expanded={strategyInfoExpanded}
                        onToggleExpanded={() => setStrategyInfoExpanded((v) => !v)}
                        variant="question"
                        resultsKeywords={null}
                    />
                    <div className="mt-10 text-center text-sm text-gray-300">
                        {loading || qcmLoadError ? (
                            qcmLoadError || 'Loading flashcard…'
                        ) : (
                            'Loading flashcard…'
                        )}
                    </div>
                    {qcmLoadError ? (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-gray-600 text-sm text-gray-100"
                                onClick={() => {
                                    void loadNextQCM({ studyModeOverride: 'flashcard', preferFlashBank: true });
                                }}
                            >
                                Retry flashcard
                            </button>
                        </div>
                    ) : null}
                </div>
                {renderRevisionChromeStack()}
            </div>
        );
    }

    // Interface normale : Question + Options (sans résultat)
    return (
        <div 
            ref={qcmContainerRef}
            className="mobile-qcm-container"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: '#1a1a1a',
                color: '#e5e7eb',
                WebkitTextFillColor: '#e5e7eb',
                zIndex: 9999,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
            }}
        >
            {/* Container scrollable interne */}
                <div 
                    className="mobile-scrollable-content"
                    style={{ 
                        minHeight: '100vh',
                        padding: '16px',
                        paddingBottom: '120px', // Plus d'espace pour les longues listes d'options
                        color: '#e5e7eb',
                    }}
                >
                {/* Nom de l'examen en haut à gauche */}
                <div className="fixed top-4 left-4 z-50">
                    <div className="text-sm text-gray-400 font-medium bg-black/20 px-3 py-1 rounded-lg backdrop-blur-sm border border-gray-700/30 shadow-lg">
                        {(() => {
                            const examId = sessionContext?.exam || DEFAULT_MOBILE_EXAM_ID;
                            // Convertir l'ID en nom lisible
                            return examId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        })()}
                    </div>
                </div>

                {/* Espace sous le badge examen pour éviter le chevauchement */}
                <div className="h-8 mb-1" />

                {strategyNotice ? (
                    <div className="mb-2 rounded-md border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-100/95 flex justify-between gap-2 items-start">
                        <span className="leading-snug">{strategyNotice}</span>
                        <button
                            type="button"
                            className="shrink-0 text-amber-300/90 underline text-[11px]"
                            onClick={() => setStrategyNotice('')}
                        >
                            Dismiss
                        </button>
                    </div>
                ) : null}

                <RevisionStrategyInfoBar
                    revisionStrategy={revisionStrategy}
                    isReinforcementWeaknessActive={isReinforcementWeaknessActive}
                    isTargetWeaknessActive={isTargetWeaknessActive}
                    currentQCM={currentQCM}
                    examCatalog={examCatalog}
                    onOpenStrategyModal={() => {
                        if (currentQCM?.qcm) setQcmLoadError(null);
                        setIsStrategyPanelOpen(true);
                    }}
                    expanded={strategyInfoExpanded}
                    onToggleExpanded={() => setStrategyInfoExpanded((v) => !v)}
                    variant="question"
                    resultsKeywords={null}
                />

                {/* Décompte en haut à droite (remplace l’ancien switch QCM / flash) */}
                {!emptyStrategyNoQuestion && (
                    <div className="fixed top-4 right-4 z-50 text-right">
                        <div className="text-sm">
                            <span
                                className={`${
                                    displayTime < (maxTime || 300) * 0.9
                                        ? 'text-green-400'
                                        : displayTime < (maxTime || 300)
                                          ? 'text-orange-400'
                                          : 'text-red-400'
                                }`}
                            >
                                {Math.floor(displayTime / 60)}:
                                {(displayTime % 60).toString().padStart(2, '0')}
                            </span>
                            <span className="text-blue-400">
                                {' '}
                                / {Math.floor((maxTime || 300) / 60)}:
                                {((maxTime || 300) % 60).toString().padStart(2, '0')}
                            </span>
                        </div>
                    </div>
                )}
                
                {/* Spacer pour le timer fixe et nom d'examen */}
                <div className={emptyStrategyNoQuestion ? 'h-8 mb-2' : 'h-10 mb-3'}></div>


                {qcmLoadError && (() => {
                    const isStrategyNoQuestion = qcmLoadError === NO_QUESTION_FOR_STRATEGY_MESSAGE;
                    // Trial mode (guest auto-login) : pool restreint aux QCM
                    // `extract_free`. Inviter à se connecter au lieu de proposer
                    // un changement de stratégie qui ne débloquera rien.
                    const guestNoResults = isStrategyNoQuestion && isPublicVisitor;
                    const message = guestNoResults
                        ? 'No results for this strategy in trial mode. Sign in to access more questions.'
                        : qcmLoadError;
                    return (
                        <div
                            className="mb-4 rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-3"
                            role="alert"
                            style={{
                                color: '#fde6c8',
                                backgroundColor: 'rgba(120, 70, 20, 0.18)',
                                border: '1px solid rgba(217, 159, 100, 0.35)',
                            }}
                        >
                            <span className="min-w-0 flex-1 leading-snug">{message}</span>
                            {guestNoResults && (
                                <button
                                    type="button"
                                    className="shrink-0 rounded border border-amber-300/40 bg-transparent px-2.5 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-300/10 transition-colors"
                                    onClick={() => window.dispatchEvent(new CustomEvent('request_open_login_modal'))}
                                >
                                    Sign in
                                </button>
                            )}
                            {isStrategyNoQuestion && !guestNoResults && (
                                <button
                                    type="button"
                                    className="shrink-0 rounded border border-amber-300/40 bg-transparent px-2.5 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-300/10 transition-colors"
                                    onClick={() => setIsStrategyPanelOpen(true)}
                                >
                                    Change strategy
                                </button>
                            )}
                        </div>
                    );
                })()}

                {/* Context */}
                {!emptyStrategyNoQuestion && currentQCM?.context && (
                    <div className="bg-gray-800 rounded-lg p-6 mb-6">
                        <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Context</h3>
                        <div className="text-lg leading-relaxed">
                            <SectionContent content={currentQCM.context} />
                        </div>
                    </div>
                )}

                {!emptyStrategyNoQuestion && (
                <>
                {/* Question */}
                <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <div className="mb-3 flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide shrink-0">Question</h3>
                        <div className="inline-flex items-center gap-2">
                            <div
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${
                                    currentAnsweredStatus.answeredInSession
                                        ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-200'
                                        : currentAnsweredStatus.answeredInExam
                                            ? 'border-sky-400/70 bg-sky-500/15 text-sky-200'
                                            : 'border-gray-500/55 bg-gray-700/30 text-gray-300'
                                }`}
                                title={
                                    currentAnsweredStatus.loading
                                        ? 'Checking answer history...'
                                        : currentAnsweredStatus.answeredInSession
                                            ? `Done in this session (${currentAnsweredStatus.answeredSessionCount})`
                                            : currentAnsweredStatus.answeredInExam
                                                ? `Done in this exam (${currentAnsweredStatus.answeredExamCount})`
                                                : 'New in this exam'
                                }
                            >
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.8-6.5 9.5-6.5S21.5 12 21.5 12s-3.8 6.5-9.5 6.5S2.5 12 2.5 12z" />
                                    <circle cx="12" cy="12" r="2.5" />
                                </svg>
                                <span>{currentAnsweredStatus.loading ? '...' : (currentAnsweredStatus.answeredInExam ? 'Seen' : 'New')}</span>
                            </div>
                            <button
                                type="button"
                                onClick={toggleCurrentSelectionPin}
                                className={`shrink-0 rounded-full border p-1.5 transition-colors ${
                                    isCurrentPinned
                                        ? 'border-amber-400/90 bg-amber-500/25 text-amber-100 ring-2 ring-amber-400/70 shadow-sm shadow-amber-900/40'
                                        : 'border-gray-500/55 bg-gray-700/30 text-gray-300 hover:bg-gray-600/35'
                                }`}
                                title={isCurrentPinned ? 'Remove from favorites' : 'Add to favorites'}
                                aria-label={isCurrentPinned ? 'Remove from favorites' : 'Add to favorites'}
                            >
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill={isCurrentPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8L3.6 9.7l5.8-.8L12 3.6z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="text-lg leading-relaxed mb-4">
                        <SectionContent content={currentQCM?.qcm?.question || ''} />
                    </div>
                    <p className="text-sm text-gray-400 italic">Choose an option below</p>
                </div>

                {/* Visual Effects Overlay */}
                {showConfetti && (
                    <div className="fixed inset-0 pointer-events-none z-50">
                        {/* Small stars scattered across screen */}
                        <div className="absolute top-1/6 left-1/6 text-2xl animate-pulse">⭐</div>
                        <div className="absolute top-1/4 right-1/5 text-xl animate-bounce">✨</div>
                        <div className="absolute top-1/3 left-1/4 text-lg animate-pulse">⭐</div>
                        <div className="absolute top-1/2 right-1/3 text-2xl animate-bounce">✨</div>
                        <div className="absolute bottom-1/3 left-1/5 text-xl animate-pulse">⭐</div>
                        <div className="absolute bottom-1/4 right-1/4 text-lg animate-bounce">✨</div>
                        <div className="absolute bottom-1/6 left-1/3 text-2xl animate-pulse">⭐</div>
                        <div className="absolute top-1/5 right-1/6 text-lg animate-bounce">✨</div>
                        <div className="absolute bottom-1/5 left-1/6 text-xl animate-pulse">⭐</div>
                        <div className="absolute top-2/3 right-1/5 text-lg animate-bounce">✨</div>
                    </div>
                )}
                
                {showSmoke && (
                    <div className="fixed inset-0 pointer-events-none z-50">
                        {/* Red filter with cracks overlay */}
                        <div className="absolute inset-0 bg-red-500/20 backdrop-blur-sm"></div>
                        <div className="absolute inset-0 opacity-30">
                            {/* Crack patterns */}
                            <div className="absolute top-1/4 left-1/4 w-32 h-32 border-2 border-red-400 transform rotate-12 opacity-60"></div>
                            <div className="absolute top-1/3 right-1/4 w-24 h-24 border-2 border-red-400 transform -rotate-12 opacity-60"></div>
                            <div className="absolute bottom-1/3 left-1/3 w-28 h-28 border-2 border-red-400 transform rotate-45 opacity-60"></div>
                            <div className="absolute bottom-1/4 right-1/3 w-20 h-20 border-2 border-red-400 transform -rotate-45 opacity-60"></div>
                            <div className="absolute top-1/2 left-1/2 w-16 h-16 border-2 border-red-400 transform rotate-12 opacity-60"></div>
                        </div>
                    </div>
                )}

                {/* (Search overlay removed as per rollback) */}
                {/* Affichage du nombre de bonnes réponses à sélectionner */}
                {correctAnswers.length > 1 && (
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4">
                        <p className="text-blue-400 text-sm font-medium">
                            Multiple answers: Select the {correctAnswers.length} correct options ({selectedAnswers.size}/{correctAnswers.length} selected)
                        </p>
                    </div>
                )}

                {/* Options */}
                <div className="space-y-4">
                    {allOptions.map((option, index) => {
                        const correctAnswerTexts = new Set(correctAnswers.map(ca => ca.answer));
                        const isCorrect = correctAnswerTexts.has(option.answer);
                        const isSelected = correctAnswers.length > 1 ? selectedAnswers.has(option.answer) : selectedOption === option.answer;
                        const incorrectOptionKey = makeIncorrectOptionKey(option.answer, index);
                        const isMarkedIncorrect = markedIncorrect.has(incorrectOptionKey);
                        const selectedCertaintyForThisOption = optionCertaintyArray.find(e => e.option === option.answer)?.certainty;
                        return (
                            <div
                                key={index}
                                className={`bg-gray-700 rounded-lg border transition-colors relative ${
                                    // Highlight only when selected with certainty
                                    isSelected
                                        ? (isCorrect
                                        ? 'border-green-500 bg-green-900/20'
                                            : 'border-red-500 bg-red-900/20')
                                                : 'border-gray-600 hover:border-gray-500'
                                }`}
                                onMouseEnter={() => setHoveredOption(index)}
                                onMouseLeave={() => setHoveredOption(null)}
                            >
                                {/* Option Text */}
                                <div className="p-4 pr-12">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="flex-grow min-w-0">
                                            <p className="mobile-qcm-option-text text-gray-200 leading-relaxed">
                                            {option.answer}
                                        </p>
                                        </div>
                                        {/* Bouton poubelle pour marquer comme incorrect */}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleToggleIncorrect(incorrectOptionKey, !isMarkedIncorrect);
                                            }}
                                            className={`shrink-0 ml-3 w-6 h-6 rounded flex items-center justify-center transition-colors ${
                                                isMarkedIncorrect 
                                                    ? 'text-red-400' 
                                                    : 'text-gray-500 hover:text-red-400'
                                            }`}
                                            title={isMarkedIncorrect ? 'Mark as correct' : 'Mark as incorrect'}
                                        >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Certainty Levels - Barres horizontales adaptées mobile */}
                                <div className="p-3">
                                    {/* Labels au-dessus des zones de certitude - pour chaque option */}
                                        <div className="flex space-x-2 mb-2">
                                            <div className="flex-1 text-center">
                                                <div className="text-xs text-gray-400 font-light">Perhaps</div>
                                            </div>
                                            <div className="flex-1 text-center">
                                                <div className="text-xs text-gray-400 font-light">Likely</div>
                                            </div>
                                            <div className="flex-1 text-center">
                                                <div className="text-xs text-gray-400 font-light">Certainly</div>
                                            </div>
                                        </div>
                                    
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleOptionSelect(option, 'low')}
                                            disabled={loading}
                                            className={`flex-1 h-8 bg-gray-600 rounded-lg transition-all duration-200 hover:bg-red-500/20 flex items-center justify-center ${isSelected && selectedCertaintyForThisOption === 'low' ? 'bg-red-500/30 border border-red-500/50' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                            title={t('low') || 'Faible'}
                                            onMouseEnter={() => setHoveredCertainty({ option: index, level: 'low' })}
                                            onMouseLeave={() => setHoveredCertainty({})}
                                        >
                                            <svg className={`w-4 h-4 ${isSelected && selectedCertaintyForThisOption === 'low' ? 'text-red-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => handleOptionSelect(option, 'medium')}
                                            disabled={loading}
                                            className={`flex-1 h-8 bg-gray-600 rounded-lg transition-all duration-200 hover:bg-yellow-500/20 flex items-center justify-center ${isSelected && selectedCertaintyForThisOption === 'medium' ? 'bg-yellow-500/30 border border-yellow-500/50' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                            title={t('medium') || 'Moyen'}
                                            onMouseEnter={() => setHoveredCertainty({ option: index, level: 'medium' })}
                                            onMouseLeave={() => setHoveredCertainty({})}
                                        >
                                            <div className="flex space-x-1">
                                                <svg className={`w-3 h-3 ${isSelected && selectedCertaintyForThisOption === 'medium' ? 'text-yellow-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                                </svg>
                                                <svg className={`w-3 h-3 ${isSelected && selectedCertaintyForThisOption === 'medium' ? 'text-yellow-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                                </svg>
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => handleOptionSelect(option, 'high')}
                                            disabled={loading}
                                            className={`flex-1 h-8 bg-gray-600 rounded-lg transition-all duration-200 hover:bg-green-500/20 flex items-center justify-center ${isSelected && selectedCertaintyForThisOption === 'high' ? 'bg-green-500/30 border border-green-500/50' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                            title={t('high') || 'Élevé'}
                                            onMouseEnter={() => setHoveredCertainty({ option: index, level: 'high' })}
                                            onMouseLeave={() => setHoveredCertainty({})}
                                        >
                                            <div className="flex space-x-1">
                                                <svg className={`w-3 h-3 ${isSelected && selectedCertaintyForThisOption === 'high' ? 'text-green-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                                </svg>
                                                <svg className={`w-3 h-3 ${isSelected && selectedCertaintyForThisOption === 'high' ? 'text-green-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                                </svg>
                                                <svg className={`w-3 h-3 ${isSelected && selectedCertaintyForThisOption === 'high' ? 'text-green-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.818a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                                </svg>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-4">
                        <div className="inline-flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-blue-400">Processing answer...</span>
                        </div>
                    </div>
                )}
                </>
                )}
                </div>
            {renderRevisionChromeStack()}
        </div>
    );
};

export default MobileQCM;
